import { Injectable, signal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DEFAULT_FONT_FAMILY, assertExists } from 'common';
import {
  ArrowCap,
  BLACK,
  CapStyle,
  Color,
  DocumentAnnotation,
  DocumentAnnotations,
  EllipseAnnotation,
  EndCapStyle,
  Font,
  ImageAnnotation,
  LineAnnotation,
  LineAppearance,
  LineStyle,
  PagesOfDocumentAnnotations,
  PathAnnotation,
  Point,
  RED,
  RectangleAnnotation,
  StampAnnotation,
  TextAnnotation,
  WHITE,
  createArrowCap,
  createFont,
  createLineAppearance,
} from 'models';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  merge,
  of,
} from 'rxjs';
import { v4 as uuid } from 'uuid';
import { assertSupportedAnnotation } from '../models';
import { PdfViewerComponent } from '../modules/pdf-viewer';
import { ApplicationQuery } from '../state/application/application.query';
import { AuthenticationService } from './authentication.service';
import { SupportedFeaturesService } from './supported-features.service';

type ZoneEventTarget = EventTarget & {
  removeAllListeners: () => void;
};

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DocumentAnnotationService {
  /**
   * Observable readonly array of annotations.
   *
   * This array is not mutable and must be copied, modified, and stored using the annotations setter.
   */
  annotations$ = new BehaviorSubject<ReadonlyArray<DocumentAnnotation>>([]);
  /**
   * Gets an immutable array of annotations.
   *
   * @returns An immutable array of annotations.
   */
  get annotations(): ReadonlyArray<DocumentAnnotation> {
    const annotations: ReadonlyArray<DocumentAnnotation> =
      this.annotations$.getValue();
    return annotations;
  }

  /**
   * Sets the value of the annotations array.
   *
   * Updating the array requires a spread, modify, then use of this set.
   * @example
   * ``` typescript
   * const annotations = [...annotationService.annotations]
   * annotations.push(newAnnotation);
   * annotationService.annotations = annotations;
   * ```
   */
  set annotations(value: DocumentAnnotations) {
    this.annotations$.next(value);
  }
  /** Annotation wrapper element to put annotations in. */
  annotationWrapper: HTMLDivElement;
  /** Canvas for document annotations. */
  annotationDrawingCanvas: HTMLCanvasElement;
  /** Pushed to whenever an annotation changes. */
  annotationChanged$ = new Subject<void>();
  /** Canvas for document annotations. */
  documentAnnotationContext: CanvasRenderingContext2D;
  /** Dirty Status. */
  dirty$ = new BehaviorSubject<boolean>(false);
  /** Current page number. */
  pageNumber = 1;
  /** Observable for the zoom scale. */
  zoomScale$ = new BehaviorSubject<number>(1);
  /** Observable for the scale factor. */
  scaleFactor$ = new BehaviorSubject<number>(0.24);
  /** Observable for annotation Properties. */
  annotationProperties$ = new Subject<DocumentAnnotation>();
  /** Cached annotations to increase performance. */
  cachedAnnotations: DocumentAnnotations | undefined;
  /** Container for thumbnails. */
  thumbnailContainer$ = new BehaviorSubject<Element | null>(null);
  /** Currently selected handle. */
  private currentHandle: number | null = null;
  /** A copy of the annotation that has been cut or copied. */
  private copiedAnnototaion?: DocumentAnnotation;
  /** Canvas creation/resize observer subscription. This is used to cleanup subscriptions. */
  private createCanvasSubscription?: Subscription;
  /** Is the mouse dragging an annotation. */
  private dragging = false;
  /** Is the mouse dragging an annotation handle. */
  private draggingHandle = false;
  /** Cache for storing Html Image Elements. */
  private imageCache: Map<string, HTMLImageElement> = new Map();
  /** Is an annotation being drawn. */
  private isDrawing = false;
  /** Data when drawing annotations. */
  private drawingShape: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  } = { startX: 0, startY: 0, endX: 0, endY: 0 };
  /** Context Menu Element. */
  private menuElement: HTMLElement;
  /** Size of annotation transformation handles. */
  private handleSize = 6;
  /** Observable for what thumbnails are showing. */
  thumbRange$ = new BehaviorSubject<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });
  /** Current range of thumbnails showing. */
  thumbRange: { start: number; end: number };
  private _enablePointerEvents = signal(false);
  /** Determines if point events are currently enabled for the active annotation canvas. */
  readonly pointerEventsEnabled =
    this._enablePointerEvents.asReadonly(); /** Pdf viewer component. */
  private pageElement: HTMLDivElement;
  /** Annotation Permissions. */
  private annotationPermissions: boolean = false;
  /** Modify Document Permissions. */
  modifyDocumentPermissions: boolean = false;
  /** Is the annotation being resized. */
  private resizing = false;
  /** Scale change subscription. This is used to cleanup subscriptions. */
  private scaleChangeSubscription?: Subscription;
  /** Viewer scale change event subscription. This is used to cleanup subscriptions. */
  private scaleChangeViewerEventSubscription: Subscription | undefined;
  /** Currently selected annotation. */
  private selectedAnnotation: DocumentAnnotation | undefined;
  /** Starting mouse position. */
  private startMousePos = { x: 0, y: 0 };
  /** Thumbnail changes subscription. This is used to cleanup subscriptions. */
  private thumbnailChangesSubscription: Subscription | undefined;
  /** Current amount of zoom on the pdf. */
  private zoomScale = 1;
  /** Scale based on the conversion of ppi on the pdf. Default is 72/300. */
  private scaleFactor = 0.24;
  /** Function for stopping the current annotation drawing. */
  private stopCurrentDrawing: () => void;
  /** Flag for monitoring if a drawing is occuring. */
  private drawingEnabled = false;
  /** Does the version of GS support annotations. */
  private annotationsSupported: boolean;

  constructor(
    private logger: NGXLogger,
    private appQuery: ApplicationQuery,
    private authService: AuthenticationService,
    private supportedFeatures: SupportedFeaturesService
  ) {}

  /**
   * Adds one or more annotations to the array.
   *
   * @param annotationOrAnnotations Annotation or annotations to add.
   */
  addAnnotations(
    annotationOrAnnotations: DocumentAnnotation | DocumentAnnotations
  ): void {
    const annotations = this.annotations.concat(annotationOrAnnotations);
    this.annotations = annotations;
  }

  /** Enables pointer events on the annotation drawing canvas. */
  enablePointerEvents(): void {
    this.setPointerEvents(true);
  }

  /** Disables pointer events on the annotation drawing canvas. */
  disablePointEvents(): void {
    this.setPointerEvents(false);
  }

  /**
   * Sets the pointer events state on the annotation drawing canvas.
   *
   * @param state - Whether pointer events should be enabled or disabled.
   */
  setPointerEvents(state: boolean): void {
    this._enablePointerEvents.set(state);
    this.annotationDrawingCanvas.style.pointerEvents = state ? 'auto' : 'none';
  }

  /** Toggles the pointer events state on the annotation drawing canvas. */
  togglePointerEvents(): void {
    this.setPointerEvents(!this.pointerEventsEnabled());
  }

  /**
   * Returns the annotations scaled. transformed and rotated.
   * Having the original list makes reseting rotation back to 0 simpler.
   */
  transformedAnnotations(zoom: number, scale: number): DocumentAnnotations {
    const annotations = this.annotations.map((anno) => {
      assertSupportedAnnotation(anno);
      // Return x, y, width, height based on the zoom and scale factor.
      let transformedAnno = this.cloneAnnotation(anno);
      assertSupportedAnnotation(transformedAnno);
      const allScale = zoom * scale;
      transformedAnno.height = anno.height * allScale;
      transformedAnno.width = anno.width * allScale;

      if (
        transformedAnno instanceof EllipseAnnotation &&
        anno instanceof EllipseAnnotation
      ) {
        transformedAnno.xScale = anno.xScale * allScale;
        transformedAnno.yScale = anno.yScale * allScale;
      }

      if (
        transformedAnno instanceof LineAnnotation &&
        anno instanceof LineAnnotation
      ) {
        transformedAnno.width = (anno.end.x - anno.x) * allScale;
        transformedAnno.height = (anno.end.y - anno.y) * allScale;
        transformedAnno.x = anno.x * allScale;
        transformedAnno.y = anno.y * allScale;
        transformedAnno.end.x = anno.end.x * allScale;
        transformedAnno.end.y = anno.end.y * allScale;
        this.transformRotatedAnnotation(transformedAnno, anno, zoom, scale);

        return transformedAnno;
      }

      if (transformedAnno.rotation !== 0) {
        // Annotation's rotation.
        this.transformRotatedAnnotation(transformedAnno, anno, zoom, scale);
      } else {
        transformedAnno.x = anno.x * allScale + transformedAnno.width / 2;
        transformedAnno.y = anno.y * allScale + transformedAnno.height / 2;
      }

      if (transformedAnno instanceof PathAnnotation) {
        transformedAnno.pathPoints = transformedAnno.pathPoints.map(
          (point) => point * zoom * scale
        );

        const x = this.getFreehandX(transformedAnno.pathPoints);
        const y = this.getFreehandY(transformedAnno.pathPoints);
        const width = this.getFreehandWidth(x, transformedAnno.pathPoints);
        const height = this.getFreehandHeight(y, transformedAnno.pathPoints);
        transformedAnno.x += x;
        transformedAnno.y += y;
        transformedAnno.width = width;
        transformedAnno.height = height;
        this.transformRotatedAnnotation(transformedAnno, anno, zoom, scale);
      }

      return transformedAnno;
    });
    return annotations;
  }

  /**
   * Clones the passed annotation and returns the clone.
   * @param annotation - Annotation to be cloned.
   * @returns - Cloned Annotation.
   */
  cloneAnnotation(annotation: DocumentAnnotation): DocumentAnnotation {
    let clonedAnnotation = undefined;
    if (annotation instanceof RectangleAnnotation) {
      clonedAnnotation = new RectangleAnnotation(
        annotation.page,
        annotation.x,
        annotation.y,
        annotation.height,
        annotation.width,
        createLineAppearance(annotation.border),
        annotation.color,
        annotation.rotation,
        annotation.visible,
        annotation.uuid,
        annotation.isHighlight
      );
    } else if (annotation instanceof TextAnnotation) {
      clonedAnnotation = new TextAnnotation(
        annotation.page,
        annotation.x,
        annotation.y,
        annotation.width,
        annotation.height,
        annotation.fontSize,
        annotation.alignment,
        annotation.text,
        annotation.color,
        annotation.uuid,
        annotation.font,
        createLineAppearance(annotation.border),
        annotation.fillColor,
        annotation.rotation
      );
    } else if (annotation instanceof StampAnnotation) {
      clonedAnnotation = new StampAnnotation(
        annotation.page,
        annotation.x,
        annotation.y,
        annotation.width,
        annotation.height,
        annotation.fontSize,
        annotation.text,
        annotation.uuid,
        annotation.color,
        annotation.font,
        annotation.rotation,
        annotation.visible,
        createLineAppearance(annotation.border),
        annotation.cornerRadius
      );
    } else if (annotation instanceof ImageAnnotation) {
      clonedAnnotation = new ImageAnnotation(
        annotation.page,
        annotation.x,
        annotation.y,
        annotation.height,
        annotation.width,
        annotation.image,
        annotation.imageHeight,
        annotation.imageWidth,
        annotation.rotation,
        annotation.opacity,
        annotation.visible,
        annotation.uuid
      );
    } else if (annotation instanceof PathAnnotation) {
      clonedAnnotation = new PathAnnotation(
        annotation.page,
        annotation.x,
        annotation.y,
        annotation.color,
        annotation.thickness,
        annotation.rotation,
        annotation.visible,
        annotation.pathPoints,
        annotation.height,
        annotation.width,
        annotation.uuid
      );
    } else if (annotation instanceof LineAnnotation) {
      clonedAnnotation = new LineAnnotation(
        annotation.page,
        annotation.x,
        annotation.y,
        { x: annotation.end.x, y: annotation.end.y },
        createLineAppearance(annotation.line),
        annotation.length,
        true,
        annotation.uuid,
        annotation.rotation,
        annotation.width,
        annotation.height,
        createArrowCap(annotation.startcap.size, annotation.startcap.style),
        createArrowCap(annotation.endcap.size, annotation.endcap.style)
      );
    } else if (annotation instanceof EllipseAnnotation) {
      clonedAnnotation = new EllipseAnnotation(
        annotation.page,
        annotation.x,
        annotation.y,
        annotation.xScale,
        annotation.yScale,
        createLineAppearance(annotation.border),
        annotation.color,
        annotation.rotation,
        annotation.visible,
        annotation.uuid,
        annotation.height,
        annotation.width
      );
    } else {
      this.logger.error(
        'Unable to clone annotation. Annotation type does not exist.'
      );
    }

    if (clonedAnnotation) {
      clonedAnnotation.burn = annotation.burn;
    }

    return clonedAnnotation || annotation;
  }

  // Converts annotations from old GS so they are in the correct place in new viewer.
  private transformRotatedAnnotation(
    anno: DocumentAnnotation,
    origAnno: DocumentAnnotation,
    zoom: number,
    scale: number
  ) {
    assertSupportedAnnotation(anno);
    anno.x = origAnno.x * zoom * scale;
    anno.y = origAnno.y * zoom * scale;
    const dx = anno.width / 2;
    const dy = anno.height / 2;
    // Needs to be a radian.
    const rotation = (anno.rotation * Math.PI) / 180;
    (anno.x = anno.x + (dx * Math.cos(rotation) - dy * Math.sin(rotation))),
      (anno.y = anno.y + (dx * Math.sin(rotation) + dy * Math.cos(rotation)));
  }

  /**
   * Reverses any rotations that have been done to the passed annotation.
   *
   * @param transformedAnno - Annotation.
   * @param canvasWidth - Canvas Width.
   * @param canvasHeight - Canvas Height.
   * @returns - Unrotated annotation.
   */
  private reverseAnnotationRotation(
    transformedAnno: DocumentAnnotation,
    canvasWidth: number,
    canvasHeight: number
  ) {
    assertSupportedAnnotation(transformedAnno);
    if (transformedAnno.rotation > 0) {
      const oldX = transformedAnno.x;
      const oldW = transformedAnno.width;
      switch (transformedAnno.rotation) {
        case 90:
          transformedAnno.x = transformedAnno.y;
          transformedAnno.y = canvasWidth - oldX - oldW;
          transformedAnno.width = transformedAnno.height;
          transformedAnno.height = oldW;
          break;

        case 180:
          transformedAnno.x =
            canvasWidth - transformedAnno.x - transformedAnno.width;
          transformedAnno.y =
            canvasHeight - transformedAnno.y - transformedAnno.height;
          break;

        case 270:
          transformedAnno.x =
            canvasHeight - transformedAnno.y - transformedAnno.height;
          transformedAnno.y = oldX;
          transformedAnno.width = transformedAnno.height;
          transformedAnno.height = oldW;
          break;

        default:
          break;
      }
    }
    return transformedAnno;
  }

  /**
   * Enables highlight drawing on the document
   *
   * @param highlight - Highlight properties
   */
  enableDrawRectangle(
    event: MouseEvent,
    color: Color,
    isHighlight: boolean = false
  ) {
    if (this.drawingEnabled) {
      this.stopCurrentDrawing();
    }
    this.drawingEnabled = true;
    this.setPointerEvents(true);
    const lineAppearance = new LineAppearance(
      CapStyle.Butt,
      BLACK,
      LineStyle.Solid,
      1
    );
    let rectangle = new RectangleAnnotation(
      0,
      0,
      0,
      0,
      0,
      lineAppearance,
      color,
      0,
      true,
      uuid(),
      isHighlight
    );
    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'crosshair';
    const drawRectangle = (event: MouseEvent, stop = false) => {
      if (!this.isDrawing) return;

      // End point of mouse drag
      this.drawingShape.endX = event.offsetX;
      this.drawingShape.endY = event.offsetY;

      // Width and height of the annotation
      const width = this.drawingShape.endX - this.drawingShape.startX;
      const height = this.drawingShape.endY - this.drawingShape.startY;

      if (stop && (height === 0 || width === 0)) {
        this.logger.debug(
          'User drew an annotation with a width or height of 0. Discarding it.'
        );
        return;
      }

      // Clear the drawing canvas
      this.documentAnnotationContext.clearRect(
        0,
        0,
        this.annotationDrawingCanvas.width,
        this.annotationDrawingCanvas.height
      );

      // Set properties
      rectangle.x = this.drawingShape.startX;
      rectangle.y = this.drawingShape.startY;
      rectangle.height = height;
      rectangle.width = width;
      rectangle.page = this.getPageNumberIndex();

      // Draw highlight
      this.documentAnnotationContext.beginPath();
      this.documentAnnotationContext.rect(
        this.drawingShape.startX,
        this.drawingShape.startY,
        width,
        height
      );
      // Color
      this.documentAnnotationContext.fillStyle = rectangle.color.rgba;
      this.documentAnnotationContext.fill();
      this.redrawAnnotations(false, false);
      if (stop) {
        // Doing some math here so the new annotations match the scaling of any imported annotations from old GS.
        let rectangleScaled = this.cloneAnnotation(rectangle);
        rectangleScaled = this.reverseAnnotationRotation(
          rectangleScaled,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        ) as RectangleAnnotation;
        rectangleScaled.x /= this.zoomScale * this.scaleFactor;
        rectangleScaled.y /= this.zoomScale * this.scaleFactor;
        rectangleScaled.height /= this.zoomScale * this.scaleFactor;
        rectangleScaled.width /= this.zoomScale * this.scaleFactor;
        this.addAnnotations(rectangleScaled);
        // Move x and y to the center
        rectangle.x = rectangle.x + rectangle.width / 2;
        rectangle.y = rectangle.y + rectangle.height / 2;
        // Set annotation as selected.
        this.selectedAnnotation = rectangle;
        // Redraw with updated x and y
        this.redrawAnnotations(true);
        this.setDirtyStatus(true);
        stop = true;
        this.annotationChanged$.next();
      }
    };

    const startDrawing = (event: MouseEvent) => {
      this.isDrawing = true;
      this.drawingShape.startX = event.offsetX;
      this.drawingShape.startY = event.offsetY;
    };

    const stopDrawing = (event: MouseEvent) => {
      if (this.isDrawing) {
        drawRectangle(event, true);
        this.isDrawing = false;
        removeListeners();
        this.stopCurrentDrawing = () => {};
      }
    };

    const cancelDrawing = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingEnabled) {
        // Clear the drawing canvas
        this.documentAnnotationContext.clearRect(
          0,
          0,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        );
        this.redrawAnnotations(false, false);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
      }
    };

    const removeListeners = () => {
      this.annotationDrawingCanvas.style.cursor = 'default';
      this.annotationDrawingCanvas.removeEventListener(
        'mousedown',
        startDrawing
      );
      this.annotationDrawingCanvas.removeEventListener(
        'mousemove',
        drawRectangle
      );
      this.annotationDrawingCanvas.removeEventListener('mouseup', stopDrawing);
      this.annotationDrawingCanvas.removeEventListener(
        'mouseout',
        cancelDrawing
      );
    };

    this.annotationDrawingCanvas.addEventListener('mousedown', startDrawing);
    this.annotationDrawingCanvas.addEventListener('mousemove', drawRectangle);
    this.annotationDrawingCanvas.addEventListener('mouseup', stopDrawing);
    this.annotationDrawingCanvas.addEventListener('mouseout', cancelDrawing);

    this.stopCurrentDrawing = () => cancelDrawing(event);
  }

  enableDrawEllipse(event: MouseEvent) {
    if (this.drawingEnabled) {
      this.stopCurrentDrawing();
    }
    this.drawingEnabled = true;
    this.setPointerEvents(true);
    const lineAppearance = new LineAppearance(
      CapStyle.Butt,
      BLACK,
      LineStyle.Solid,
      2
    );
    let ellipse = new EllipseAnnotation(
      0,
      0,
      0,
      0,
      0,
      lineAppearance,
      WHITE,
      0,
      true,
      uuid(),
      0,
      0
    );
    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'crosshair';
    const drawEllipse = (event: MouseEvent, stop = false) => {
      if (!this.isDrawing) return;

      // End point of mouse drag
      this.drawingShape.endX = event.offsetX;
      this.drawingShape.endY = event.offsetY;

      // Width and height of the annotation
      const width = this.drawingShape.endX - this.drawingShape.startX;
      const height = this.drawingShape.endY - this.drawingShape.startY;

      if (stop && (height === 0 || width === 0)) {
        this.logger.debug(
          'User drew an annotation with a width or height of 0. Discarding it.'
        );
        return;
      }

      // Clear the drawing canvas
      this.documentAnnotationContext.clearRect(
        0,
        0,
        this.annotationDrawingCanvas.width,
        this.annotationDrawingCanvas.height
      );

      // Set properties
      ellipse.x = this.drawingShape.startX + width / 2;
      ellipse.y = this.drawingShape.startY + height / 2;
      ellipse.height = height;
      ellipse.width = width;
      ellipse.page = this.getPageNumberIndex();

      this.drawEllipseAnnotation(
        ellipse,
        this.documentAnnotationContext,
        this.scaleFactor
      );

      this.redrawAnnotations(false, false);
      if (stop) {
        // Doing some math here so the new annotations match the scaling of any imported annotations from old GS.
        let ellipseScaled = this.cloneAnnotation(ellipse);
        ellipseScaled = this.reverseAnnotationRotation(
          ellipseScaled,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        ) as EllipseAnnotation;
        ellipseScaled.x =
          (ellipse.x - width / 2) / (this.zoomScale * this.scaleFactor);
        ellipseScaled.y =
          (ellipse.y - height / 2) / (this.zoomScale * this.scaleFactor);
        ellipseScaled.height /= this.zoomScale * this.scaleFactor;
        ellipseScaled.width /= this.zoomScale * this.scaleFactor;
        ellipseScaled.xScale = ellipseScaled.width / 2;
        ellipseScaled.yScale = ellipseScaled.height / 2;
        this.addAnnotations(ellipseScaled);
        // Set annotation as selected.
        this.selectedAnnotation = ellipse;
        // Redraw with updated x and y
        this.redrawAnnotations(true);
        this.setDirtyStatus(true);
        stop = true;
        this.annotationChanged$.next();
      }
    };

    const startDrawing = (event: MouseEvent) => {
      this.isDrawing = true;
      this.drawingShape.startX = event.offsetX;
      this.drawingShape.startY = event.offsetY;
    };

    const stopDrawing = (event: MouseEvent) => {
      if (this.isDrawing) {
        drawEllipse(event, true);
        this.isDrawing = false;
        removeListeners();
        this.stopCurrentDrawing = () => {};
      }
    };

    const cancelDrawing = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingEnabled) {
        // Clear the drawing canvas
        this.documentAnnotationContext.clearRect(
          0,
          0,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        );
        this.redrawAnnotations(false, false);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
      }
    };

    const removeListeners = () => {
      this.annotationDrawingCanvas.style.cursor = 'default';
      this.annotationDrawingCanvas.removeEventListener(
        'mousedown',
        startDrawing
      );
      this.annotationDrawingCanvas.removeEventListener(
        'mousemove',
        drawEllipse
      );
      this.annotationDrawingCanvas.removeEventListener('mouseup', stopDrawing);
      this.annotationDrawingCanvas.removeEventListener(
        'mouseout',
        cancelDrawing
      );
    };

    this.annotationDrawingCanvas.addEventListener('mousedown', startDrawing);
    this.annotationDrawingCanvas.addEventListener('mousemove', drawEllipse);
    this.annotationDrawingCanvas.addEventListener('mouseup', stopDrawing);
    this.annotationDrawingCanvas.addEventListener('mouseout', cancelDrawing);

    this.stopCurrentDrawing = () => cancelDrawing(event);
  }

  /**
   * Cleans up listeners and objects related to rendering annotations.
   *
   * This function should **always** be called on destroy of anything that calls initializeService.
   */
  destroyAnnotations(): void {
    this.logger.debug('Cleaning up annotations.');
    this.selectedAnnotation = undefined;
    this.annotations = [];
    this.createCanvasSubscription?.unsubscribe();
    this.scaleChangeSubscription?.unsubscribe();
    this.thumbnailChangesSubscription?.unsubscribe();
    this.scaleChangeViewerEventSubscription?.unsubscribe();
    this.thumbnailContainer$.next(null);
    (
      this.annotationDrawingCanvas as HTMLCanvasElement & ZoneEventTarget
    )?.removeAllListeners?.();
    this.annotationDrawingCanvas?.remove?.();
    this.cachedAnnotations = undefined;
    this.imageCache.clear();
    this.setDirtyStatus(false);
  }

  /**
   * Enable note drawing on the page.
   *
   * @param event - Mouse Event
   */
  enableDrawNote(event: MouseEvent) {
    if (this.drawingEnabled) {
      this.stopCurrentDrawing();
    }
    this.drawingEnabled = true;
    this.setPointerEvents(true);
    let note = new TextAnnotation(
      this.pageNumber,
      0,
      0,
      0,
      0,
      70,
      'left',
      '',
      BLACK,
      uuid(),
      createFont({
        size: this.appQuery.annotationNoteDefaultFontSize,
        name: this.appQuery.annotationNoteDefaultFontName,
      })
    );
    note.border.color = Color.convertFromHex(
      BLACK.hexCode,
      this.appQuery.annotationNoteDefaultOutlineOpacity
    );
    note.border.thickness = 1;
    note.fillColor = Color.convertFromHex(
      this.appQuery.annotationNoteDefaultFillColor,
      1
    );

    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'crosshair';
    const drawNote = (event: MouseEvent, stop = false) => {
      if (!this.isDrawing) return;

      // End point of mouse drag
      this.drawingShape.endX = event.offsetX;
      this.drawingShape.endY = event.offsetY;

      // Width and height of the annotation
      const width = this.drawingShape.endX - this.drawingShape.startX;
      const height = this.drawingShape.endY - this.drawingShape.startY;

      if (stop && (height === 0 || width === 0)) {
        this.logger.debug(
          'User drew an annotation with a width or height of 0. Discarding it.'
        );
        return;
      }

      // Clear the drawing canvas
      this.documentAnnotationContext.clearRect(
        0,
        0,
        this.annotationDrawingCanvas.width,
        this.annotationDrawingCanvas.height
      );

      // Set properties
      note.x = this.drawingShape.startX;
      note.y = this.drawingShape.startY;
      note.height = height;
      note.width = width;
      note.page = this.getPageNumberIndex();

      // Draw note
      this.documentAnnotationContext.beginPath();
      this.documentAnnotationContext.fillStyle = note.fillColor.rgba;
      this.documentAnnotationContext.fillRect(
        this.drawingShape.startX,
        this.drawingShape.startY,
        width,
        height
      );
      // Rectangle stroke.
      this.documentAnnotationContext.strokeStyle = note.border.color.rgba;
      this.documentAnnotationContext.lineWidth = note.border.thickness;
      this.documentAnnotationContext.strokeRect(
        this.drawingShape.startX,
        this.drawingShape.startY,
        width,
        height
      );

      this.redrawAnnotations(false, false);

      if (stop) {
        // Doing some math here so the new annotations match the scaling of any imported annotations from old GS.
        let noteScaled = this.cloneAnnotation(note);
        noteScaled = this.reverseAnnotationRotation(
          noteScaled,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        ) as TextAnnotation;
        noteScaled.x /= this.zoomScale * 0.24;
        noteScaled.y /= this.zoomScale * 0.24;
        noteScaled.height /= this.zoomScale * 0.24;
        noteScaled.width /= this.zoomScale * 0.24;
        this.addAnnotations(noteScaled);
        // Move x and y to the center
        note.x = note.x + note.width / 2;
        note.y = note.y + note.height / 2;

        // Set annotation as selected.
        this.selectedAnnotation = note;

        // Redraw with updated x and y
        this.redrawAnnotations(true);
        stop = true;
        this.setDirtyStatus(true);
        if (note.text) {
          this.annotationChanged$.next();
        } else {
          // Show note properties.
          this.showProperties();
        }
      }
    };

    const startDrawing = (event: MouseEvent) => {
      this.isDrawing = true;
      this.drawingShape.startX = event.offsetX;
      this.drawingShape.startY = event.offsetY;
    };

    const stopDrawing = (event: MouseEvent) => {
      if (this.isDrawing) {
        drawNote(event, true);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
        this.stopCurrentDrawing = () => {};
      }
    };

    const cancelDrawing = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingEnabled) {
        this.documentAnnotationContext.clearRect(
          0,
          0,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        );
        this.redrawAnnotations(false, false);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
      }
    };

    const removeListeners = () => {
      this.annotationDrawingCanvas.style.cursor = 'default';
      this.annotationDrawingCanvas.removeEventListener(
        'mousedown',
        startDrawing
      );
      this.annotationDrawingCanvas.removeEventListener('mousemove', drawNote);
      this.annotationDrawingCanvas.removeEventListener('mouseup', stopDrawing);
      this.annotationDrawingCanvas.removeEventListener(
        'mouseout',
        cancelDrawing
      );
    };

    this.annotationDrawingCanvas.addEventListener('mousedown', startDrawing);
    this.annotationDrawingCanvas.addEventListener('mousemove', drawNote);
    this.annotationDrawingCanvas.addEventListener('mouseup', stopDrawing);
    this.annotationDrawingCanvas.addEventListener('mouseout', cancelDrawing);

    this.stopCurrentDrawing = () => cancelDrawing(event);
  }

  /**
   * Enables stamp drawing on the page.
   *
   * @param event - Mouse Event.
   * @param text - Stamp Text.
   */
  enableDrawStamp(event: MouseEvent, text: string) {
    if (this.drawingEnabled) {
      this.stopCurrentDrawing();
    }
    this.drawingEnabled = true;
    this.setPointerEvents(true);
    let stamp = new StampAnnotation(
      0,
      0,
      0,
      0,
      0,
      70,
      text,
      uuid(),
      Color.convertFromHex(
        this.appQuery.annotationStampDefaultColor,
        this.appQuery.annotationStampDefaultOpacity
      ),
      DEFAULT_FONT_FAMILY,
      0,
      true,
      new LineAppearance(
        CapStyle.Butt,
        Color.convertFromHex(
          this.appQuery.annotationStampDefaultColor,
          this.appQuery.annotationStampDefaultOpacity
        ),
        LineStyle.Solid,
        this.appQuery.annotationStampDefaultThickness,
        EndCapStyle.None
      ),
      0
    );

    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'crosshair';
    const drawStamp = (event: MouseEvent, stop = false) => {
      if (!this.isDrawing) return;

      // End point of mouse drag
      this.drawingShape.endX = event.offsetX;
      this.drawingShape.endY = event.offsetY;

      // Width and height of the annotation
      const width = this.drawingShape.endX - this.drawingShape.startX;
      const height = this.drawingShape.endY - this.drawingShape.startY;

      if (stop && (height === 0 || width === 0)) {
        this.logger.debug(
          'User drew an annotation with a width or height of 0. Discarding it.'
        );
        return;
      }

      // Clear the drawing canvas - Clearing the whole thing here because the stroke and text make it wonky.
      this.documentAnnotationContext.clearRect(
        0,
        0,
        this.annotationDrawingCanvas.width,
        this.annotationDrawingCanvas.height
      );

      // Set properties
      stamp.x = this.drawingShape.startX;
      stamp.y = this.drawingShape.startY;
      stamp.height = height;
      stamp.width = width;
      stamp.page = this.getPageNumberIndex();

      this.documentAnnotationContext.save();
      this.documentAnnotationContext.translate(stamp.x, stamp.y);

      stamp.text = this.stampReplacement(stamp.text);

      // Draw stamp stroke
      this.documentAnnotationContext.beginPath();
      this.documentAnnotationContext.strokeStyle = stamp.color.rgba;
      this.documentAnnotationContext.lineWidth = stamp.border.thickness / 3;
      this.documentAnnotationContext.strokeRect(0, 0, width, height);
      // Color
      this.documentAnnotationContext.fillStyle = stamp.color.rgba;
      // Adjust font size to fit the rectangle
      this.documentAnnotationContext.font = `bold ${Math.abs(stamp.height) - 5}px ${stamp.font}, helvetica, sans-serif`;
      this.documentAnnotationContext.textBaseline = 'middle';
      let textWidth =
        this.documentAnnotationContext.measureText(stamp.text).width + 4;
      let scale = stamp.width / textWidth;
      this.documentAnnotationContext.scale(scale, 1);
      // Draw stamp text
      this.documentAnnotationContext.fillText(stamp.text, 2, height / 2);
      this.documentAnnotationContext.restore();
      // Redraw all the annotations because we had to clear everything to draw the stamp.
      this.redrawAnnotations(false, false);
      if (stop) {
        // Doing some math here so the new annotations match the scaling of any imported annotations from old GS.
        let stampScaled = this.cloneAnnotation(stamp);
        stampScaled = this.reverseAnnotationRotation(
          stampScaled,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        ) as StampAnnotation;
        stampScaled.x /= this.zoomScale * this.scaleFactor;
        stampScaled.y /= this.zoomScale * this.scaleFactor;
        stampScaled.height /= this.zoomScale * this.scaleFactor;
        stampScaled.width /= this.zoomScale * this.scaleFactor;
        this.addAnnotations(stampScaled);
        // Move x and y to the center
        stamp.x = stamp.x + stamp.width / 2;
        stamp.y = stamp.y + stamp.height / 2;

        // Set annotation as selected.
        this.selectedAnnotation = stamp;
        // Redraw with updated x and y
        this.redrawAnnotations(true);
        this.setDirtyStatus(true);
        stop = true;
        if (stamp.text === 'CUSTOM') {
          // Show note properties.
          this.showProperties();
        } else {
          this.annotationChanged$.next();
        }
      }
    };

    const startDrawing = (event: MouseEvent) => {
      this.isDrawing = true;
      this.drawingShape.startX = event.offsetX;
      this.drawingShape.startY = event.offsetY;
    };

    const stopDrawing = (event: MouseEvent) => {
      if (this.isDrawing) {
        drawStamp(event, true);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
        this.stopCurrentDrawing = () => {};
      }
    };

    const cancelDrawing = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingEnabled) {
        this.documentAnnotationContext.clearRect(
          0,
          0,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        );
        this.redrawAnnotations(false, false);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
      }
    };

    const removeListeners = () => {
      this.annotationDrawingCanvas.style.cursor = 'default';
      this.annotationDrawingCanvas.removeEventListener(
        'mousedown',
        startDrawing
      );
      this.annotationDrawingCanvas.removeEventListener('mousemove', drawStamp);
      this.annotationDrawingCanvas.removeEventListener('mouseup', stopDrawing);
      this.annotationDrawingCanvas.removeEventListener(
        'mouseout',
        cancelDrawing
      );
    };

    this.annotationDrawingCanvas.addEventListener('mousedown', startDrawing);
    this.annotationDrawingCanvas.addEventListener('mousemove', drawStamp);
    this.annotationDrawingCanvas.addEventListener('mouseup', stopDrawing);
    this.annotationDrawingCanvas.addEventListener('mouseout', cancelDrawing);

    this.stopCurrentDrawing = () => cancelDrawing(event);
  }

  /**
   * Replacements in stamp text.
   * @param textValue - Stamp value.
   * @returns - Text with text replacement.
   */
  private stampReplacement(textValue: string): string {
    let text = textValue;
    if (text.toLowerCase().includes('@today')) {
      const currentMoment = moment();
      const replaceDateString = currentMoment.format('M/DD/YYYY h:mm:ss A');
      text = text.replace(/@today/i, replaceDateString);
    }
    if (text.toLowerCase().includes('@user')) {
      text = text.replace(/@user/i, this.authService.user.name);
    }
    if (text.toLowerCase().includes('@date')) {
      const replaceDateString = moment().format('M/DD/YYYY');
      text = text.replace(/@date/i, replaceDateString);
    }
    if (text.toLowerCase().includes('@time')) {
      const replaceDateString = moment().format('LT');
      text = text.replace(/@time/i, replaceDateString);
    }

    return text;
  }

  /**
   * Copies an annotation to the center of the page.
   *
   * @param e - Mouse event.
   * @param annotation - Annotation to be duplicated.
   */
  duplicateAnnotation(e: MouseEvent): void {
    assertExists(this.selectedAnnotation);
    const index = this.getAnnotationIndex(this.selectedAnnotation);
    let annoToDupe = this.annotations[index];
    assertSupportedAnnotation(annoToDupe);

    const scale = this.scaleFactor * this.zoomScale;
    const canvasCenterX = this.annotationDrawingCanvas.width / scale / 2;
    const canvasCenterY = this.annotationDrawingCanvas.height / scale / 2;

    const rotation = (annoToDupe.rotation * Math.PI) / 180;
    const halfWidth = annoToDupe.width / 2;
    const halfHeight = annoToDupe.height / 2;

    annoToDupe = this.cloneAnnotation(annoToDupe);
    assertSupportedAnnotation(annoToDupe);

    // Correct center for rotations
    annoToDupe.x =
      canvasCenterX -
      halfWidth * Math.cos(rotation) +
      halfHeight * Math.sin(rotation);
    annoToDupe.y =
      canvasCenterY -
      halfWidth * Math.sin(rotation) -
      halfHeight * Math.cos(rotation);

    if (annoToDupe instanceof LineAnnotation) {
      annoToDupe.end.x = annoToDupe.x + annoToDupe.width;
      annoToDupe.end.y = annoToDupe.y + annoToDupe.height;
    }
    annoToDupe.uuid = uuid();

    this.addAnnotations(annoToDupe);
    this.setDirtyStatus(true);
    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'default';
    this.redrawAnnotations(true);
    this.annotationChanged$.next();
  }

  freeHandPoints: number[];

  enableDrawFreehand(event: MouseEvent) {
    if (this.drawingEnabled) {
      this.stopCurrentDrawing();
    }
    this.drawingEnabled = true;
    this.setPointerEvents(true);
    let freehand = new PathAnnotation(
      0,
      0,
      0,
      BLACK,
      15,
      0,
      true,
      [],
      0,
      0,
      uuid()
    );
    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'crosshair';
    const drawFreehand = (event: MouseEvent, stop = false) => {
      if (!this.isDrawing) return;

      // End point of mouse drag
      this.drawingShape.endX = event.offsetX;
      this.drawingShape.endY = event.offsetY;

      // Set propertie
      freehand.page = this.getPageNumberIndex();

      this.documentAnnotationContext.lineTo(event.offsetX, event.offsetY);
      this.documentAnnotationContext.stroke();

      this.freeHandPoints.push(event.offsetX);
      this.freeHandPoints.push(event.offsetY);

      if (stop) {
        this.documentAnnotationContext.closePath();
        // Doing some math here so the new annotations match the scaling of any imported annotations from old GS.
        let freehandScaled = this.cloneAnnotation(freehand);
        freehandScaled = this.reverseAnnotationRotation(
          freehandScaled,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        ) as PathAnnotation;
        const x = this.getFreehandX(this.freeHandPoints);
        const y = this.getFreehandY(this.freeHandPoints);
        const width = this.getFreehandWidth(x, this.freeHandPoints);
        const height = this.getFreehandHeight(y, this.freeHandPoints);
        if (stop && (height === 0 || width === 0)) {
          this.logger.debug(
            'User drew an annotation with a width or height of 0. Discarding it.'
          );
          return;
        }
        freehandScaled.width = this.getFreehandWidth(x, this.freeHandPoints);
        freehandScaled.height = this.getFreehandHeight(y, this.freeHandPoints);
        freehandScaled.height /= this.zoomScale * this.scaleFactor;
        freehandScaled.width /= this.zoomScale * this.scaleFactor;
        freehandScaled.x = x / (this.zoomScale * this.scaleFactor);
        freehandScaled.y = y / (this.zoomScale * this.scaleFactor);
        freehandScaled.pathPoints = this.freeHandPoints;
        for (
          let i = 0, c = freehandScaled.pathPoints.length;
          i < c;
          i = i + 2
        ) {
          freehandScaled.pathPoints[i] =
            freehandScaled.pathPoints[i] / (this.zoomScale * this.scaleFactor) -
            freehandScaled.x /**-
            freehandScaled.width / 2*/;
          freehandScaled.pathPoints[i + 1] =
            freehandScaled.pathPoints[i + 1] /
              (this.zoomScale * this.scaleFactor) -
            freehandScaled.y /**-
             freehandScaled.height / 2*/;
        }
        this.addAnnotations(freehandScaled);
        // Move x and y to the center
        freehand.x = x + width / 2;
        freehand.y = y + height / 2;
        freehand.width = width;
        freehand.height = height;
        // Set annotation as selected.
        this.selectedAnnotation = freehand;
        // Redraw with updated x and y
        this.redrawAnnotations(true);
        this.setDirtyStatus(true);
        stop = true;
        this.annotationChanged$.next();
      }
    };

    const startDrawing = (event: MouseEvent) => {
      this.freeHandPoints = [];
      this.isDrawing = true;
      this.drawingShape.startX = event.offsetX;
      this.drawingShape.startY = event.offsetY;
      this.freeHandPoints.push(event.offsetX);
      this.freeHandPoints.push(event.offsetY);
      this.documentAnnotationContext.lineWidth =
        freehand.thickness * this.scaleFactor;
      this.documentAnnotationContext.beginPath();
      this.documentAnnotationContext.stroke();
    };

    const stopDrawing = (event: MouseEvent) => {
      if (this.isDrawing) {
        drawFreehand(event, true);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
        this.stopCurrentDrawing = () => {};
      }
    };

    const cancelDrawing = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingEnabled) {
        this.documentAnnotationContext.clearRect(
          0,
          0,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        );
        this.redrawAnnotations(false, false);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
      }
    };

    const removeListeners = () => {
      this.annotationDrawingCanvas.style.cursor = 'default';
      this.annotationDrawingCanvas.removeEventListener(
        'mousedown',
        startDrawing
      );
      this.annotationDrawingCanvas.removeEventListener(
        'mousemove',
        drawFreehand
      );
      this.annotationDrawingCanvas.removeEventListener('mouseup', stopDrawing);
      this.annotationDrawingCanvas.removeEventListener(
        'mouseout',
        cancelDrawing
      );
    };

    this.annotationDrawingCanvas.addEventListener('mousedown', startDrawing);
    this.annotationDrawingCanvas.addEventListener('mousemove', drawFreehand);
    this.annotationDrawingCanvas.addEventListener('mouseup', stopDrawing);
    this.annotationDrawingCanvas.addEventListener('mouseout', cancelDrawing);

    this.stopCurrentDrawing = () => cancelDrawing(event);
  }

  getFreehandWidth = (x: number, freeHandPoints: number[]) => {
    let largest = Number.NEGATIVE_INFINITY;
    let i = 0;
    for (const point of freeHandPoints) {
      if (largest < point && i % 2 === 0) {
        largest = point;
      }
      i = i + 1;
    }
    return largest - x;
  };

  getFreehandX = (freeHandPoints: number[]) => {
    let smallest = Number.POSITIVE_INFINITY;
    let i = 0;
    for (const point of freeHandPoints) {
      if (smallest > point && i % 2 === 0) {
        smallest = point;
      }
      i = i + 1;
    }
    return smallest;
  };

  getFreehandHeight = (y: number, freeHandPoints: number[]) => {
    let largest = Number.NEGATIVE_INFINITY;
    let i = 0;
    for (const point of freeHandPoints) {
      if (largest < point && i % 2 !== 0) {
        largest = point;
      }
      i = i + 1;
    }
    return largest - y;
  };

  getFreehandY = (freeHandPoints: number[]) => {
    let smallest = Number.POSITIVE_INFINITY;
    let i = 0;
    for (const point of freeHandPoints) {
      if (smallest > point && i % 2 !== 0) {
        smallest = point;
      }
      i = i + 1;
    }
    return smallest;
  };

  /**
   * Enable image drawing on canvas.
   *
   * @param event - Mouse Event.
   * @param annotation - Image Annotation.
   */
  enableDrawImage(event: MouseEvent) {
    if (this.drawingEnabled) {
      this.stopCurrentDrawing();
    }
    this.drawingEnabled = true;
    this.setPointerEvents(true);
    let image = new ImageAnnotation(
      0,
      0,
      0,
      0,
      0,
      '',
      0,
      0,
      0,
      1,
      true,
      uuid()
    );
    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'crosshair';
    const drawImage = (event: MouseEvent, stop = false) => {
      if (!this.isDrawing) return;

      // End point of mouse drag
      this.drawingShape.endX = event.offsetX;
      this.drawingShape.endY = event.offsetY;

      // Width and height of the annotation
      const width = this.drawingShape.endX - this.drawingShape.startX;
      const height = this.drawingShape.endY - this.drawingShape.startY;

      if (stop && (height === 0 || width === 0)) {
        this.logger.debug(
          'User drew an annotation with a width or height of 0. Discarding it.'
        );
        return;
      }

      // Clear the drawing canvas
      this.documentAnnotationContext.clearRect(
        0,
        0,
        this.annotationDrawingCanvas.width,
        this.annotationDrawingCanvas.height
      );
      // Set properties
      image.x = this.drawingShape.startX;
      image.y = this.drawingShape.startY;
      image.height = height;
      image.width = width;
      image.page = this.getPageNumberIndex();

      this.documentAnnotationContext.save();

      // Draw highlight
      this.documentAnnotationContext.beginPath();
      this.documentAnnotationContext.strokeRect(
        this.drawingShape.startX,
        this.drawingShape.startY,
        width,
        height
      );
      // Color
      this.documentAnnotationContext.fillStyle = '#000000';
      this.documentAnnotationContext.fill();
      this.documentAnnotationContext.restore();
      // Redraw all the annotations because we had to clear everything to draw the image.
      this.redrawAnnotations(false, false);
      if (stop) {
        // Doing some math here so the new annotations match the scaling of any imported annotations from old GS.
        let imageScaled = this.cloneAnnotation(image);
        imageScaled = this.reverseAnnotationRotation(
          imageScaled,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        ) as ImageAnnotation;
        imageScaled.x /= this.zoomScale * this.scaleFactor;
        imageScaled.y /= this.zoomScale * this.scaleFactor;
        imageScaled.height /= this.zoomScale * this.scaleFactor;
        imageScaled.width /= this.zoomScale * this.scaleFactor;
        this.addAnnotations(imageScaled);
        // Move x and y to the center
        image.x = image.x + image.width / 2;
        image.y = image.y + image.height / 2;
        // Set annotation as selected.
        this.selectedAnnotation = image;
        if (!image.image) {
          this.openFilePicker(image);
        }
        // Redraw with updated x and y
        this.redrawAnnotations(true);
        this.setDirtyStatus(true);
        stop = true;
      }
    };

    const startDrawing = (event: MouseEvent) => {
      this.isDrawing = true;
      this.drawingShape.startX = event.offsetX;
      this.drawingShape.startY = event.offsetY;
    };

    const stopDrawing = (event: MouseEvent) => {
      if (this.isDrawing) {
        drawImage(event, true);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
        this.stopCurrentDrawing = () => {};
      }
    };

    const cancelDrawing = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingEnabled) {
        this.documentAnnotationContext.clearRect(
          0,
          0,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        );
        this.redrawAnnotations(false, false);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
      }
    };

    const removeListeners = () => {
      this.annotationDrawingCanvas.style.cursor = 'default';
      this.annotationDrawingCanvas.removeEventListener(
        'mousedown',
        startDrawing
      );
      this.annotationDrawingCanvas.removeEventListener('mousemove', drawImage);
      this.annotationDrawingCanvas.removeEventListener('mouseup', stopDrawing);
      this.annotationDrawingCanvas.removeEventListener(
        'mouseout',
        cancelDrawing
      );
    };

    this.annotationDrawingCanvas.addEventListener('mousedown', startDrawing);
    this.annotationDrawingCanvas.addEventListener('mousemove', drawImage);
    this.annotationDrawingCanvas.addEventListener('mouseup', stopDrawing);
    this.annotationDrawingCanvas.addEventListener('mouseout', cancelDrawing);

    this.stopCurrentDrawing = () => cancelDrawing(event);
  }

  /**
   * Opens the file picker and draws the image on the canvas.
   *
   * @param image - Image Annotation
   */
  openFilePicker(imageAnnotation: ImageAnnotation) {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/png, image/jpeg';
    fileInput.style.display = 'none';

    fileInput.addEventListener('change', (event) => {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.files && input.files[0]) {
        const file = input.files[0];
        const img = new Image();

        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target?.result as string;
          img.onload = () => {
            const x = imageAnnotation.x - imageAnnotation.width / 2;
            const y = imageAnnotation.y - imageAnnotation.height / 2;
            this.documentAnnotationContext.clearRect(
              x - 1,
              y - 1,
              imageAnnotation.width + 1,
              imageAnnotation.height + 1
            );
            this.documentAnnotationContext.drawImage(
              img,
              x,
              y,
              imageAnnotation.width,
              imageAnnotation.height
            );

            // Set the image in the annotation object.
            const index = this.getAnnotationIndex(imageAnnotation);
            (this.annotations[index] as ImageAnnotation).image = img.src;
            if (this.cachedAnnotations) {
              (this.cachedAnnotations[index] as ImageAnnotation).image =
                img.src;
            }
            (this.selectedAnnotation as ImageAnnotation).image = img.src;
          };
          img.onerror = (error) => {
            this.logger.error('Error reading file:', error);
          };
        };
        reader.onerror = (error) => {
          this.logger.error('Error reading file:', error);
        };
        reader.readAsDataURL(file);

        // We have to push to the subject here to get the updated annotation with the image.
        this.annotationChanged$.next();
      }
    });
    // Trigger the file input
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }

  /**
   * Enables highlight drawing on the document
   *
   * @param highlight - Highlight properties
   */
  enableDrawLine(event: MouseEvent) {
    if (this.drawingEnabled) {
      this.stopCurrentDrawing();
    }
    this.drawingEnabled = true;
    this.setPointerEvents(true);
    const lineAppearance = new LineAppearance(
      CapStyle.Butt,
      RED,
      LineStyle.Solid,
      15
    );
    let line = new LineAnnotation(
      this.getPageNumberIndex(),
      0,
      0,
      { x: 0, y: 0 },
      lineAppearance,
      0,
      true,
      uuid(),
      0,
      0,
      0,
      createArrowCap({ height: 15, width: 15 }, EndCapStyle.None),
      createArrowCap({ height: 15, width: 15 }, EndCapStyle.FilledArrow)
    );
    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'crosshair';
    const drawLine = (event: MouseEvent, stop = false) => {
      if (!this.isDrawing) return;

      // End point of mouse drag
      this.drawingShape.endX = event.offsetX;
      this.drawingShape.endY = event.offsetY;

      // Width and height of the annotation
      const width = this.drawingShape.endX - this.drawingShape.startX;
      const height = this.drawingShape.endY - this.drawingShape.startY;

      if (stop && (height === 0 || width === 0)) {
        this.logger.debug(
          'User drew an annotation with a width or height of 0. Discarding it.'
        );
        return;
      }

      // Clear the drawing canvas
      this.documentAnnotationContext.clearRect(
        0,
        0,
        this.annotationDrawingCanvas.width,
        this.annotationDrawingCanvas.height
      );

      // Set properties
      line.x = this.drawingShape.startX + width / 2;
      line.y = this.drawingShape.startY + height / 2;
      line.end.x = this.drawingShape.endX;
      line.end.y = this.drawingShape.endY;
      line.height = height;
      line.width = width;

      this.drawLineAnnotation(
        line,
        this.documentAnnotationContext,
        this.scaleFactor
      );

      this.redrawAnnotations(false, false);
      if (stop) {
        // Doing some math here so the new annotations match the scaling of any imported annotations from old GS.
        let lineScaled = this.cloneAnnotation(line);
        lineScaled = this.reverseAnnotationRotation(
          lineScaled,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        ) as LineAnnotation;
        lineScaled.x =
          (line.x - width / 2) / (this.zoomScale * this.scaleFactor);
        lineScaled.y =
          (line.y - height / 2) / (this.zoomScale * this.scaleFactor);
        lineScaled.end.x /= this.zoomScale * this.scaleFactor;
        lineScaled.end.y /= this.zoomScale * this.scaleFactor;
        lineScaled.height /= this.zoomScale * this.scaleFactor;
        lineScaled.width /= this.zoomScale * this.scaleFactor;
        this.addAnnotations(lineScaled);
        // Move x and y to the center
        // Set annotation as selected.
        this.selectedAnnotation = line;
        // Redraw with updated x and y
        this.redrawAnnotations(true);
        this.setDirtyStatus(true);
        stop = true;
        this.annotationChanged$.next();
      }
    };

    const startDrawing = (event: MouseEvent) => {
      this.isDrawing = true;
      this.drawingShape.startX = event.offsetX;
      this.drawingShape.startY = event.offsetY;
    };

    const stopDrawing = (event: MouseEvent) => {
      if (this.isDrawing) {
        drawLine(event, true);
        this.isDrawing = false;
        removeListeners();
        this.stopCurrentDrawing = () => {};
      }
    };

    const cancelDrawing = (event: MouseEvent) => {
      if (this.isDrawing || this.drawingEnabled) {
        this.documentAnnotationContext.clearRect(
          0,
          0,
          this.annotationDrawingCanvas.width,
          this.annotationDrawingCanvas.height
        );
        this.redrawAnnotations(false, false);
        this.isDrawing = false;
        removeListeners();
        this.drawingEnabled = false;
      }
    };

    const removeListeners = () => {
      this.annotationDrawingCanvas.style.cursor = 'default';
      this.annotationDrawingCanvas.removeEventListener(
        'mousedown',
        startDrawing
      );
      this.annotationDrawingCanvas.removeEventListener('mousemove', drawLine);
      this.annotationDrawingCanvas.removeEventListener('mouseup', stopDrawing);
      this.annotationDrawingCanvas.removeEventListener(
        'mouseout',
        cancelDrawing
      );
    };

    this.annotationDrawingCanvas.addEventListener('mousedown', startDrawing);
    this.annotationDrawingCanvas.addEventListener('mousemove', drawLine);
    this.annotationDrawingCanvas.addEventListener('mouseup', stopDrawing);
    this.annotationDrawingCanvas.addEventListener('mouseout', cancelDrawing);

    this.stopCurrentDrawing = () => cancelDrawing(event);
  }

  /**
   * Clears and draws all annotations.
   *
   * @param reCache - Recache the annotations.
   * @param clear - Whether to clear the annotations. Defaults to true.
   */
  redrawAnnotations(reCache: boolean = false, clear: boolean = true): void {
    if (reCache) this.cachedAnnotations = undefined;
    if (!this.documentAnnotationContext) return;

    if (clear) {
      // Clear the drawing canvas.
      this.clearCanvas();
    }
    // If we don't do some caching performance gets real bad.
    const annotationsList =
      this.cachedAnnotations ||
      this.transformedAnnotations(this.zoomScale, this.scaleFactor);
    this.cachedAnnotations = annotationsList;

    //Reset annotation selection.
    if (this.selectedAnnotation) {
      const index = this.getAnnotationIndex(this.selectedAnnotation);
      this.selectedAnnotation = this.cachedAnnotations[index];
    }

    // Draw annotations.
    for (const anno of annotationsList) {
      if (!anno || anno.page !== this.getPageNumberIndex()) continue;
      this.drawAnnotation(anno);
    }

    // Draw handles on the selected annotation.
    if (this.selectedAnnotation) {
      this.drawHandles();
    }
  }

  /**
   * Returns the current pages index.
   *
   * @returns number
   */
  private getPageNumberIndex() {
    return this.pageNumber - 1;
  }

  /**
   * Updates page annotations on page change.
   *
   * @param pdfViewerComponent - PdfViewerComponent
   * @param pageNumber - Page to load.
   */
  changePage(pdfViewerComponent: PdfViewerComponent, pageNumber: number) {
    // Clears selections on the previous page before changing.
    this.selectedAnnotation = undefined;
    // Set the page element so we can add and remove canvases
    this.pageElement = pdfViewerComponent.pageElement;
    this.pageNumber = pageNumber;
    // Recreate canvas in listeners. They're detroyed on page change.
    this.createCanvasSubscription?.unsubscribe();
    this.createCanvasSubscription = this.createCanvas(true).subscribe(() => {
      this.attachCanvasListeners();
      // Make sure the zoom scale is correct.
      this.zoomScale$.next(
        pdfViewerComponent.pdfJsComponent.PDFViewerApplication.pdfViewer._pages[
          this.getPageNumberIndex()
        ].viewport.scale
      );
      this.redrawAnnotations();
    });
  }

  /**
   * Draws an annotation on the canvas.
   *
   * @param annotation - Annotation
   */
  drawAnnotation(annotation: DocumentAnnotation) {
    if (annotation instanceof RectangleAnnotation) {
      this.drawRectangleAnnotation(
        annotation,
        this.documentAnnotationContext,
        this.scaleFactor
      );
    } else if (annotation instanceof StampAnnotation) {
      this.drawStampAnnotation(
        annotation,
        this.documentAnnotationContext,
        this.scaleFactor
      );
    } else if (annotation instanceof TextAnnotation) {
      this.drawTextAnnotation(
        annotation,
        this.documentAnnotationContext,
        this.scaleFactor
      );
    } else if (annotation instanceof ImageAnnotation) {
      this.drawImageAnnotation(annotation, this.documentAnnotationContext);
    } else if (annotation instanceof PathAnnotation) {
      this.drawFreehandAnnotation(
        annotation,
        this.documentAnnotationContext,
        this.scaleFactor
      );
    } else if (annotation instanceof LineAnnotation) {
      this.drawLineAnnotation(
        annotation,
        this.documentAnnotationContext,
        this.scaleFactor
      );
    } else if (annotation instanceof EllipseAnnotation) {
      this.drawEllipseAnnotation(
        annotation,
        this.documentAnnotationContext,
        this.scaleFactor
      );
    }
  }

  /**
   * Draw a rectangle annotation.
   *
   * @param annotation - Annotation.
   */
  private drawRectangleAnnotation(
    annotation: RectangleAnnotation,
    context: CanvasRenderingContext2D,
    pageScale: number
  ) {
    // Save the context
    context.save();

    // Apply transformations and draw
    context.translate(annotation.x, annotation.y);
    context.rotate((annotation.rotation * Math.PI) / 180);
    context.beginPath();

    // Set the fillstyle
    context.strokeStyle = annotation.border.color.rgba;
    context.lineWidth =
      (annotation.border.thickness / 3) * (pageScale / this.scaleFactor);
    if (!annotation.isHighlight) {
      context.strokeRect(
        -annotation.width / 2,
        -annotation.height / 2,
        annotation.width,
        annotation.height
      );
    }
    // Set the fillstyle
    context.fillStyle = annotation.color.rgba;
    context.fillRect(
      -annotation.width / 2,
      -annotation.height / 2,
      annotation.width,
      annotation.height
    );

    context.closePath();

    // Restore the context
    context.restore();
  }

  /**
   * Draw a ellipse annotation.
   *
   * @param annotation - Annotation.
   */
  private drawEllipseAnnotation(
    annotation: EllipseAnnotation,
    context: CanvasRenderingContext2D,
    pageScale: number
  ) {
    // Save the context
    context.save();
    // Set the fillstyle
    context.fillStyle = annotation.color.rgba;
    context.strokeStyle = annotation.border.color.rgba;
    context.lineWidth =
      (annotation.border.thickness / 3) * (pageScale / this.scaleFactor);
    // Apply transformations and draw
    context.translate(annotation.x, annotation.y);
    context.rotate((annotation.rotation * Math.PI) / 180);
    context.scale(
      Math.sign(annotation.width / 2),
      Math.sign(annotation.height / 2)
    );
    context.beginPath();
    context.ellipse(
      0,
      0,
      Math.abs(annotation.width / 2),
      Math.abs(annotation.height / 2),
      0,
      0,
      Math.PI * 2
    );

    context.closePath();
    context.stroke();
    context.fill();

    // Restore the context
    context.restore();
  }

  private drawLineAnnotation(
    annotation: LineAnnotation,
    context: CanvasRenderingContext2D,
    scale: number
  ) {
    // Save the context
    context.save();
    /** Set x,y as the drawing point.*/
    context.translate(annotation.x, annotation.y);
    context.rotate((annotation.rotation * Math.PI) / 180);
    context.strokeStyle = annotation.line.color.rgba;
    context.lineWidth = annotation.line.thickness * scale;
    context.beginPath();

    /** Adjust for x,y being the center point. */
    const translatedX = -annotation.width / 2;
    const translatedY = -annotation.height / 2;
    const translatedEndX = annotation.width / 2;
    const translatedEndY = annotation.height / 2;

    /** Draw the line. */
    context.moveTo(translatedX, translatedY);
    context.lineTo(translatedEndX, translatedEndY);
    context.stroke();
    context.closePath();
    /** End Cap. */
    this.drawCap(
      {
        x: translatedX,
        y: translatedY,
      },
      { x: translatedEndX, y: translatedEndY },
      annotation.endcap,
      context,
      annotation.line.color.rgba,
      scale
    );
    /** Start Cap. */
    this.drawCap(
      { x: translatedEndX, y: translatedEndY },
      {
        x: translatedX,
        y: translatedY,
      },
      annotation.startcap,
      context,
      annotation.line.color.rgba,
      scale
    );

    // Restore the context
    context.restore();
  }

  private drawCap(
    start: Point,
    end: Point,
    cap: ArrowCap,
    context: CanvasRenderingContext2D,
    color: string,
    pageScale: number
  ) {
    if (cap.style === EndCapStyle.None) return;
    if (
      cap.style === EndCapStyle.FilledArrow ||
      cap.style === EndCapStyle.Arrow
    ) {
      this.drawArrowCap(start, end, cap, context, color, pageScale);
    } else if (cap.style === EndCapStyle.Diamond) {
      this.drawDiamondCap(start, end, cap, context, color, pageScale);
    } else if (cap.style === EndCapStyle.FilledEllipse) {
      this.drawEllipseCap(start, end, cap, context, color, pageScale);
    }
  }

  drawArrowCap(
    start: Point,
    end: Point,
    cap: ArrowCap,
    context: CanvasRenderingContext2D,
    color: string,
    pageScale: number
  ) {
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    // Calculate the tip of the triangle (offset past the end of the line)
    const scaledHeight =
      cap.size.height * (pageScale / this.scaleFactor) * this.zoomScale;
    const scaledWidth =
      cap.size.width * (pageScale / this.scaleFactor) * this.zoomScale;

    /** You might ask yourself, "Why 2.5?", this was just to make the sizes of the caps consistent with what was in GSW. */
    const scaledTipHeight =
      cap.style === EndCapStyle.FilledArrow ? scaledHeight / 2.5 : 0;
    const scaledFlairedHeight =
      cap.style === EndCapStyle.FilledArrow ? scaledHeight / 2.5 : scaledHeight;

    /** Arrow Tip. */
    const arrowTipX = end.x + scaledTipHeight * Math.cos(angle);
    const arrowTipY = end.y + scaledTipHeight * Math.sin(angle);

    /** Arrow Flairs. */
    const arrowBaseLeftX =
      end.x -
      scaledFlairedHeight * Math.cos(angle) -
      (scaledWidth / 2.5) * Math.cos(angle - Math.PI / 2);
    const arrowBaseLeftY =
      end.y -
      scaledFlairedHeight * Math.sin(angle) -
      (scaledWidth / 2.5) * Math.sin(angle - Math.PI / 2);

    const arrowBaseRightX =
      end.x -
      scaledFlairedHeight * Math.cos(angle) +
      (scaledWidth / 2.5) * Math.cos(angle - Math.PI / 2);
    const arrowBaseRightY =
      end.y -
      scaledFlairedHeight * Math.sin(angle) +
      (scaledWidth / 2.5) * Math.sin(angle - Math.PI / 2);

    context.beginPath();

    context.fillStyle = color;
    if (cap.style === EndCapStyle.FilledArrow) {
      context.moveTo(arrowTipX, arrowTipY);
      context.lineTo(arrowBaseLeftX, arrowBaseLeftY);
      context.lineTo(arrowBaseRightX, arrowBaseRightY);
      context.closePath();
      context.fill();
    } else if (cap.style === EndCapStyle.Arrow) {
      context.moveTo(arrowTipX, arrowTipY);
      context.lineTo(arrowBaseLeftX, arrowBaseLeftY);
      context.moveTo(arrowTipX, arrowTipY);
      context.lineTo(arrowBaseRightX, arrowBaseRightY);
      context.closePath();
      context.stroke();
    }
  }

  drawEllipseCap(
    start: Point,
    end: Point,
    cap: ArrowCap,
    context: CanvasRenderingContext2D,
    color: string,
    pageScale: number
  ) {
    const angle = Math.atan2(end.y - start.y, end.x - start.x);

    // Center of the ellipse is the endpoint of the line
    const ellipseCenterX = end.x;
    const ellipseCenterY = end.y;

    // Ellipse dimensions
    const ellipseWidth =
      cap.size.width * (pageScale / this.scaleFactor) * this.zoomScale;
    const ellipseHeight =
      cap.size.height * (pageScale / this.scaleFactor) * this.zoomScale;

    context.save();
    context.translate(ellipseCenterX, ellipseCenterY);
    context.rotate(angle);
    context.beginPath();
    context.ellipse(
      0,
      0,
      ellipseWidth / 2.5,
      ellipseHeight / 2.5,
      0,
      0,
      2 * Math.PI
    );
    context.fillStyle = color;
    context.fill();
    context.restore();
  }

  drawDiamondCap(
    start: Point,
    end: Point,
    cap: ArrowCap,
    context: CanvasRenderingContext2D,
    color: string,
    pageScale: number
  ) {
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const diamondCenterX = end.x;
    const diamondCenterY = end.y;

    const diamondWidth =
      cap.size.width * (pageScale / this.scaleFactor) * this.zoomScale;
    const diamondHeight =
      cap.size.height * (pageScale / this.scaleFactor) * this.zoomScale;

    const topVertexX = diamondCenterX + (diamondHeight / 2.5) * Math.cos(angle);
    const topVertexY = diamondCenterY + (diamondHeight / 2.5) * Math.sin(angle);
    const bottomVertexX =
      diamondCenterX - (diamondHeight / 2.5) * Math.cos(angle);
    const bottomVertexY =
      diamondCenterY - (diamondHeight / 2.5) * Math.sin(angle);
    const leftVertexX =
      diamondCenterX - (diamondWidth / 2.5) * Math.cos(angle - Math.PI / 2);
    const leftVertexY =
      diamondCenterY - (diamondWidth / 2.5) * Math.sin(angle - Math.PI / 2);
    const rightVertexX =
      diamondCenterX + (diamondWidth / 2.5) * Math.cos(angle - Math.PI / 2);
    const rightVertexY =
      diamondCenterY + (diamondWidth / 2.5) * Math.sin(angle - Math.PI / 2);

    context.beginPath();
    /** Set starting point. */
    context.moveTo(topVertexX, topVertexY);
    context.lineTo(leftVertexX, leftVertexY);
    context.lineTo(bottomVertexX, bottomVertexY);
    context.lineTo(rightVertexX, rightVertexY);
    context.closePath();
    context.fillStyle = color;
    context.fill();
  }

  /**
   * Draw a note annotation.
   *
   * @param annotation - Annotation.
   */
  private drawStampAnnotation(
    annotation: StampAnnotation,
    context: CanvasRenderingContext2D,
    pageScale: number
  ) {
    // Save the context
    context.save();
    // Apply transformations and draw
    context.translate(annotation.x, annotation.y);
    context.rotate((annotation.rotation * Math.PI) / 180);
    context.beginPath();

    // Draw rectangle
    context.strokeStyle = annotation.border.color.rgba;
    context.lineWidth =
      (annotation.border.thickness / 3) * (pageScale / this.scaleFactor);

    this.drawRectangleStroke(annotation, context, pageScale);

    // Adjust font size to fit the rectangle
    context.font = `bold ${Math.abs(annotation.height) - 5}px ${annotation.font}, helvetica, sans-serif`;
    context.textBaseline = 'middle';

    let textWidth = context.measureText(annotation.text).width + 4;

    let scale = annotation.width / textWidth;

    // Apply horizontal scaling
    context.scale(scale, 1);

    context.fillStyle = annotation.border.color.rgba;
    context.fillText(annotation.text, -textWidth / 2 + 2, 0);

    context.closePath();

    // Restore the context
    context.restore();
  }

  /**
   * Draws a rectangular stroke.
   *
   * @param annotation - Annotation.
   * @param context - Context.
   * @param scale - Page Scale.
   */
  private drawRectangleStroke(
    annotation: StampAnnotation,
    context: CanvasRenderingContext2D,
    scale: number
  ) {
    if (annotation.cornerRadius > 0) {
      const x = -annotation.width / 2,
        y = -annotation.height / 2,
        width = annotation.width,
        height = annotation.height,
        radius = annotation.cornerRadius * scale * this.zoomScale;

      context.beginPath();
      // Top.
      context.moveTo(x + radius, y);
      context.lineTo(x + width - radius, y);

      //Top right corner.
      context.quadraticCurveTo(x + width, y, x + width, y + radius);

      // Right.
      context.lineTo(x + width, y + height - radius);

      // Bottom right corner.
      context.quadraticCurveTo(
        x + width,
        y + height,
        x + width - radius,
        y + height
      );

      // Bottom.
      context.lineTo(x + radius, y + height);

      // Bottom left corner.
      context.quadraticCurveTo(x, y + height, x, y + height - radius);

      // Left.
      context.lineTo(x, y + radius);

      // Top left corner.
      context.quadraticCurveTo(x, y, x + radius, y); // Top-left corner
      context.closePath();
      context.stroke();
    } else {
      context.strokeRect(
        -annotation.width / 2,
        -annotation.height / 2,
        annotation.width,
        annotation.height
      );
    }
  }

  /**
   * Draw a note annotation.
   *
   * @param annotation - Annotation.
   */
  private drawTextAnnotation(
    annotation: TextAnnotation,
    context: CanvasRenderingContext2D,
    scale: number
  ) {
    // Save the context
    context.save();
    // Apply transformations and draw
    context.translate(annotation.x, annotation.y);
    context.rotate((annotation.rotation * Math.PI) / 180);
    context.beginPath();
    // Solid rectangle.
    context.fillStyle = annotation.fillColor.rgba;
    context.fillRect(
      -annotation.width / 2,
      -annotation.height / 2,
      annotation.width,
      annotation.height
    );
    // Rectangle stroke.
    context.strokeStyle = annotation.border.color.rgba;
    context.lineWidth =
      annotation.border.thickness * (scale / this.scaleFactor);
    context.strokeRect(
      -annotation.width / 2,
      -annotation.height / 2,
      annotation.width,
      annotation.height
    );
    // Font
    let font = '';
    if (annotation.font.bold) {
      font += 'bold';
    }
    if (annotation.font.italic) {
      font += ' italic';
    }
    context.font = ` ${font} ${annotation.font.size * scale * this.zoomScale}pt ${annotation.font.name}`;
    context.fillStyle = annotation.color.rgba;

    // Set text align ment in context.
    context.textAlign = annotation.alignment as CanvasTextAlign;
    // Wrap text within the rectangle.
    const wrappedText = this.getWrappedText(
      annotation.text,
      Math.abs(annotation.width),
      context
    );
    let lineCount = 0;
    for (const text of wrappedText) {
      const lineHeight = this.getTextLineHeight(context);
      const paddedLineHeight = lineHeight * 1.2;
      const textWidth = context.measureText(text).width;
      let x = 0,
        y = 0;

      x =
        -annotation.width / 2 + 4 * (scale / this.scaleFactor) * this.zoomScale;
      y =
        -annotation.height / 2 +
        paddedLineHeight +
        lineCount * paddedLineHeight;
      // Have to adjust the x for alignment within the rectangle.
      x = this.adjustXForTextAlignment(
        x,
        annotation.alignment,
        Math.abs(annotation.width),
        scale
      );

      // Convert height and width to positive.
      const adjustedWidth = Math.abs(annotation.width);
      const adjustedHeight = Math.abs(annotation.height);

      // Get offset depending on if the height or width is negative.
      const xOffset = annotation.width < 0 ? annotation.width : 0;
      const yOffset = annotation.height < 0 ? annotation.height : 0;

      // Determine if the text fix within the bounds of the note annotation.
      const fitsInRectangle =
        paddedLineHeight < adjustedHeight / (lineCount + 1) &&
        (adjustedWidth - 4) * (scale / this.scaleFactor) * this.zoomScale >
          textWidth;

      // If it fits. It ships.
      if (fitsInRectangle) {
        context.fillText(text, x + xOffset, y + yOffset, adjustedWidth);
      }

      // Need the adjusted location of x for underline and strikeout.
      const adjustments = this.adjustmentsForUnderlineAndStrike(
        x,
        textWidth,
        annotation.alignment
      );
      /** Draw stikeout and underline. */
      if (annotation.font.strikeout) {
        context.moveTo(adjustments.start, y - lineHeight / 4);
        context.lineTo(adjustments.finish, y - lineHeight / 4);
        context.strokeStyle = annotation.color.rgba;
        context.lineWidth =
          annotation.font.size * 0.025 * (scale / this.scaleFactor);
        context.stroke();
      }
      if (annotation.font.underline) {
        context.moveTo(adjustments.start, y + lineHeight / 4);
        context.lineTo(adjustments.finish, y + lineHeight / 4);
        context.strokeStyle = annotation.color.rgba;
        context.lineWidth =
          annotation.font.size * 0.025 * (scale / this.scaleFactor);
        context.stroke();
      }
      lineCount++;
      // Don't let the text extend past the bottom of the text annotation.
      if (
        (paddedLineHeight + lineHeight / 4) * lineCount >=
        Math.abs(annotation.height)
      ) {
        break;
      }
    }

    context.closePath();
    // Restore the context
    context.restore();
  }

  /**
   * Draw an image annotation.
   *
   * @param annotation - Image Annotations.
   * @param context - Context in which to draw.
   */
  private drawImageAnnotation(
    annotation: ImageAnnotation,
    context: CanvasRenderingContext2D
  ): void {
    // Check if the image cached, if not cache it.
    let image = this.imageCache.get(annotation.uuid);
    if (!image) {
      image = new Image();
      image.src = annotation.image;
      image.onload = () => {
        assertExists(image);
        this.imageCache.set(annotation.uuid, image);
        this.redrawAnnotations();
      };
    }

    if (image.complete) {
      this.renderImage(annotation, context, image);
    }
  }

  /**
   * Render the image on the canvas.
   * @param annotation - Annotation to render.
   * @param context - Context on which to render it on.
   * @param image - Image to render.
   */
  private renderImage(
    annotation: ImageAnnotation,
    context: CanvasRenderingContext2D,
    image: HTMLImageElement
  ): void {
    context.save();

    // Apply transformations and draw
    context.translate(annotation.x, annotation.y);
    context.rotate((annotation.rotation * Math.PI) / 180);
    context.drawImage(
      image,
      -annotation.width / 2,
      -annotation.height / 2,
      annotation.width,
      annotation.height
    );

    // Restore the context
    context.restore();
  }

  private drawFreehandAnnotation(
    annotation: PathAnnotation,
    context: CanvasRenderingContext2D,
    scale: number
  ) {
    // Save the context
    context.save();
    //Apply transformations and draw
    context.translate(annotation.x, annotation.y);
    context.rotate((annotation.rotation * Math.PI) / 180);
    context.strokeStyle = annotation.color.rgba;
    context.lineWidth = annotation.thickness * scale;
    context.beginPath();
    for (let i = 0, c = annotation.pathPoints; i < c.length; i = i + 2) {
      const pointX = annotation.pathPoints[i] - annotation.width / 2;
      const pointY = annotation.pathPoints[i + 1] - annotation.height / 2;

      if (i === 0) {
        // Move to the initial point
        context.moveTo(pointX, pointY);
      } else {
        // Draw lines to subsequent points
        context.lineTo(pointX, pointY);
      }
    }
    context.stroke();
    context.closePath();
    // Restore the context
    context.restore();
  }
  /**
   * Returns the start and finish location of the line based on the text width and x.
   * @param x - X coordinate of the text.
   * @param textWidth - Text width.
   * @param alignment - Text alignement.
   * @returns - Start and finish x locations of the lines.
   */
  private adjustmentsForUnderlineAndStrike(
    x: number,
    textWidth: number,
    alignment: string
  ): { start: number; finish: number } {
    if (alignment === 'center') {
      return { start: x - textWidth / 2, finish: x + textWidth / 2 };
    } else if (alignment === 'right') {
      return { start: x - textWidth, finish: x };
    }
    return { start: x, finish: x + textWidth };
  }

  /**
   * Returns the adjusted x value based on the alignment.
   * @param x - X coordinate of text.
   * @param alignment - Allignment.
   * @param maxWidth - Max width of text.
   * @returns - New adjusted x coordinate.
   */
  private adjustXForTextAlignment(
    x: number,
    alignment: string,
    maxWidth: number,
    scale: number
  ): number {
    if (alignment === 'center') {
      return x + maxWidth / 2;
    } else if (alignment === 'right') {
      // I arbitrally chose 8 here.
      return x + maxWidth - 8 * (scale / this.scaleFactor);
    }
    return x;
  }

  /**
   * Returns the current context's line height.
   * @param context - Canvas context.
   * @returns - Line Height.
   */
  private getTextLineHeight(context: CanvasRenderingContext2D): number {
    // Set it to Q for consistent heights.
    const measuredText = context.measureText('Q');
    const lineActualHeight =
      measuredText.actualBoundingBoxAscent +
      measuredText.actualBoundingBoxDescent;
    return lineActualHeight;
  }

  /**
   * Break the text into an array of strings where the
   * strings are not larger then the annotations width.
   * @param text - Text.
   * @param maxWidth - Annotation width.
   * @param context - Context.
   * @returns Array of strings.
   */
  private getWrappedText(
    text: string,
    maxWidth: number,
    context: CanvasRenderingContext2D
  ): string[] {
    const lines: string[] = [];
    const endPadding = 5;

    // Split text by new lines.
    const segments = text.split(/(\n)/);
    let currentLine = '';

    for (const segment of segments) {
      if (segment === '\n') {
        const lineBeforeClear = currentLine;
        if (currentLine.trim()) {
          lines.push(currentLine.trim());
          currentLine = '';
        }
        // If the line before is empty then it's an empty new line and we need to add it.
        if (!lineBeforeClear) {
          lines.push('');
        }
      } else {
        // Split by spaces to get each word.
        const words = segment.split(' ');
        for (const word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const metrics = context.measureText(testLine);

          // If the line is too big for the annotation width..
          if (metrics.width + endPadding > maxWidth) {
            if (currentLine.trim()) {
              lines.push(currentLine.trim());
              currentLine = '';
            }

            // The word is too big for the width of the annotation.
            const wordLength = context.measureText(word);
            if (wordLength.width + endPadding > maxWidth) {
              let partialWord = '';
              for (const letter of word) {
                partialWord += letter;
                const letterMetrics = context.measureText(partialWord);
                if (letterMetrics.width + endPadding > maxWidth) {
                  lines.push(partialWord.slice(0, -1));
                  partialWord = letter;
                }
              }
              currentLine = partialWord;
            } else {
              currentLine = word;
            }
          } else {
            currentLine = testLine;
          }
        }
      }
    }

    // Push the last line if it has content
    if (currentLine.trim()) {
      lines.push(currentLine.trim());
    }

    return lines;
  }

  /**
   * Draws handles on an annotation.
   */
  drawHandles(): void {
    const context = this.documentAnnotationContext;
    // Set handles color and stroke color.
    context.fillStyle = 'white';
    context.strokeStyle = 'black';
    // Get handle positions.
    const handlePositions = this.getHandlePositions();
    if (!handlePositions) return;
    context.lineWidth = 1;
    // Draw handles.
    for (const [index, pos] of handlePositions.entries()) {
      if (index === 4) {
        context.fillStyle = 'lime';
        context.strokeStyle = 'black';
      }
      context.beginPath();
      context.arc(pos.x, pos.y, this.handleSize / 2, 0, Math.PI * 2);
      context.fill();
      context.stroke();
      0;
    }
  }

  /**
   * Gets the positions of the handles.
   *
   * @returns - Array of handle positions x and y.
   */
  getHandlePositions(): { x: number; y: number }[] {
    let anno = this.selectedAnnotation;
    /**
     * No longer asserting here since rotate handle lays outside the annotation.
     * This would go off when an annotation was selected and the mouse went outside the
     */
    if (!anno) return [];
    const handlePositions = this.calculateHandlePositions(anno);
    return handlePositions;
  }

  /**
   * Calculate where the handles should be.
   *
   * @param anno - Annotation
   * @returns - Array of handle positions x and y.
   */
  calculateHandlePositions(
    anno: DocumentAnnotation
  ): { x: number; y: number }[] {
    assertSupportedAnnotation(anno);
    // Half width and height is the center.
    const halfWidth = anno.width / 2;
    const halfHeight = anno.height / 2;
    // Setting sin and cos for readability.
    const sin = Math.sin((anno.rotation * Math.PI) / 180);
    const cos = Math.cos((anno.rotation * Math.PI) / 180);

    return this.getHandlePositionsForRotation(
      anno,
      halfWidth,
      halfHeight,
      cos,
      sin
    );
  }

  /**
   * Get the handle positions when page is at 0 degrees.
   *
   * @param anno - Annotation.
   * @param halfWidth - Canvas half width.
   * @param halfHeight - Canvas half height.
   * @param cos - Cosine of annotation rotation.
   * @param sin  - Sine of annotation rotation.
   * @returns  - Array of handle positions x and y.
   */
  getHandlePositionsForRotation(
    anno: DocumentAnnotation,
    halfWidth: number,
    halfHeight: number,
    cos: number,
    sin: number
  ): { x: number; y: number }[] {
    const handlePositions = [
      {
        x: anno.x + (-halfWidth * cos - -halfHeight * sin),
        y: anno.y + (-halfWidth * sin + -halfHeight * cos),
      },
      {
        x: anno.x + (halfWidth * cos - -halfHeight * sin),
        y: anno.y + (halfWidth * sin + -halfHeight * cos),
      },
      {
        x: anno.x + (-halfWidth * cos - halfHeight * sin),
        y: anno.y + (-halfWidth * sin + halfHeight * cos),
      },
      {
        x: anno.x + (halfWidth * cos - halfHeight * sin),
        y: anno.y + (halfWidth * sin + halfHeight * cos),
      },
    ];

    const offSetAmount = halfHeight < 0 ? -30 : 30;

    // Offset for the handle when rotating it with the annotation.
    const rotatedOffsetX = 0 * cos + offSetAmount * sin;
    const rotatedOffsetY = 0 * sin + offSetAmount * cos;

    // Rotate handle with offset.
    const midpointX =
      (handlePositions[0].x + handlePositions[1].x) / 2 + rotatedOffsetX;
    const midpointY =
      (handlePositions[0].y + handlePositions[1].y) / 2 - rotatedOffsetY;

    // Add new handle position
    handlePositions.push({ x: midpointX, y: midpointY });

    return handlePositions;
  }

  /**
   * Get mouse position on drawing canvas.
   *
   * @param e - Mouse Event
   * @returns - Mouse coordinates { x: number, y: number }
   */
  getMousePos(e: MouseEvent): { x: number; y: number } {
    const rect = this.annotationDrawingCanvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  /**
   * Is mouse over a handle
   *
   * @param mousePos - Mouse Position - { x: number, y: number }
   * @param handlePos - Handle Position - { x: number, y: number }
   * @returns - boolean
   */
  isMouseOnHandle(
    mousePos: { x: number; y: number },
    handlePos: { x: number; y: number }
  ): boolean {
    return (
      mousePos.x > handlePos.x - this.handleSize / 2 &&
      mousePos.x < handlePos.x + this.handleSize / 2 &&
      mousePos.y > handlePos.y - this.handleSize / 2 &&
      mousePos.y < handlePos.y + this.handleSize / 2
    );
  }

  /**
   * Loads annotations into the service.
   *
   * @param annotations - Annotations to load into service.
   */
  loadAnnotations(annotations: DocumentAnnotations, reloadAfterBurn = false) {
    this.annotations = [];
    this.cachedAnnotations = undefined;
    for (let anno of annotations) {
      assertSupportedAnnotation(anno);
      if (!anno.uuid) {
        // Add an uuid if it doesn't exist.
        anno.uuid = uuid();
      }
      if (anno instanceof EllipseAnnotation && !reloadAfterBurn) {
        /** Move to top left. */
        anno.x = anno.x - anno.width / 2;
        anno.y = anno.y - anno.height / 2;
      }
      if (anno instanceof TextAnnotation) {
        if (anno.alignment === 'Near') {
          anno.alignment = 'left';
        } else if (anno.alignment === 'Center') {
          anno.alignment = 'center';
        } else if (anno.alignment === 'Far') {
          anno.alignment = 'right';
        }
      }
      if (anno instanceof LineAnnotation) {
        anno.width = anno.end.x - anno.x;
        anno.height = anno.end.y - anno.y;
      }
    }
    this.annotations = annotations;
  }

  /**
   * Reloads all existing annotations.
   *
   * This recreates the canvas and should not be done unless absolutely necessary.
   *
   * @param pdfViewerComponent - PDF Viewer Component.
   */
  reloadAnnotations(pdfViewerComponent: PdfViewerComponent): void {
    // Retrieve a mutable copy of the annotations
    const annotations = [...this.annotations];
    // Store the current dirty state.
    const dirty = this.dirty$.getValue();
    const pageNumber = this.pageNumber;
    // Grab the current thumbnailer.
    const thumbnailContainer = this.thumbnailContainer$.getValue();
    // Destroy the annotations.
    this.destroyAnnotations();
    this.initializeService(
      pdfViewerComponent,
      thumbnailContainer,
      pageNumber,
      this.annotationPermissions,
      this.modifyDocumentPermissions,
      false
    );
    // Restore the annotations.
    this.loadAnnotations(annotations);
    // Restore the dirty state.
    this.setDirtyStatus(dirty);
  }

  /**
   * Clears annotation data from the service.
   */
  clearAnnotationData() {
    this.annotations = [];
    this.cachedAnnotations = [];
  }

  /**
   * Initializes the annotation service. Creates the canvas and attaches listeners.
   *
   * @param pdfViewerComponent
   * @param pageNumber
   */
  initializeService(
    pdfViewerComponent: PdfViewerComponent,
    thumbnailContainer: Element | null,
    pageNumber: number = 1,
    annotationPermissions: boolean = false,
    modifyDocumentPermissions: boolean = false,
    saveInProgress: boolean = false
  ) {
    this.annotationsSupported = this.supportedFeatures.versionSupports(
      this.supportedFeatures.annotationsDrawing
    );
    //Clear previous selections
    this.selectedAnnotation = undefined;
    this.annotationPermissions = annotationPermissions;
    this.modifyDocumentPermissions = modifyDocumentPermissions;
    /** Disable while saving so we don't run into issues where the status changes to false before annotations save. */
    if (!saveInProgress) {
      this.setDirtyStatus(false);
    }
    // Set the page element so we can add and remove canvases
    this.pageElement = pdfViewerComponent.pageElement;
    this.thumbnailContainer$.next(thumbnailContainer);
    this.pageNumber = pageNumber;
    this.createCanvasSubscription?.unsubscribe();
    this.createCanvasSubscription = this.createCanvas().subscribe(() => {
      this.attachCanvasListeners();
      this.listenForZoomChange(pdfViewerComponent);
      this.listenForThumbnailChanges();
      this.listenForScaleChanges();
      // Set the initial zoom scale.
      this.zoomScale$.next(
        pdfViewerComponent.pdfJsComponent.PDFViewerApplication.pdfViewer._pages[
          this.getPageNumberIndex()
        ].viewport.scale
      );
      this.redrawAnnotations();
    });
  }

  /**
   * Updates the passed annotation the master array.
   * @param annotation - Annotation.
   */
  updateAnnotation(annotation: DocumentAnnotation) {
    if (!annotation) return;
    assertSupportedAnnotation(annotation);
    if (annotation instanceof StampAnnotation) {
      annotation.text = this.stampReplacement(annotation.text);
    }
    const index = this.getAnnotationIndex(annotation);
    const annotations = [...this.annotations];
    annotations[index] = annotation;
    this.annotations = annotations;
    this.redrawAnnotations(true);
    this.redrawThumbnailAnnotations(true);
    this.annotationChanged$.next();
  }

  /**
   * Listens for changes with the zoom on the current page.
   *
   * @param pdfViewerComponent - Pdf Viewer Component.
   */
  private listenForZoomChange(pdfViewerComponent: PdfViewerComponent): void {
    this.scaleChangeViewerEventSubscription?.unsubscribe();
    // Had to add debounce otherwise our annotation layer gets removed from the page.
    this.scaleChangeViewerEventSubscription =
      pdfViewerComponent.pdfJsComponent.onScaleChange
        .pipe(untilDestroyed(this), debounceTime(500))
        .subscribe((scale: number) => {
          if (
            pdfViewerComponent.pdfJsComponent.PDFViewerApplication.pdfViewer
              ._pages.length > 0
          ) {
            this.zoomScale$.next(
              pdfViewerComponent.pdfJsComponent.PDFViewerApplication.pdfViewer
                ._pages[this.getPageNumberIndex()].viewport.scale
            );
          }
        });
  }

  /**
   * Clears the currently selected annotation.
   */
  private clearSelectedAnnotation(): void {
    delete this.selectedAnnotation;
  }

  /**
   * Listens for zoom scale and scale factor changes.
   */
  private listenForScaleChanges(): void {
    const distinctScale$ = this.scaleFactor$.pipe(distinctUntilChanged());
    const distinctZoom$ = this.zoomScale$.pipe(distinctUntilChanged());
    // Handle window resize events such as browser zoom changes.
    const windowResize$ = fromEvent(window, 'resize').pipe(debounceTime(500));

    this.scaleChangeSubscription?.unsubscribe();

    // Watch the scales and update the annotations when the scales change.
    this.scaleChangeSubscription = merge(
      distinctScale$,
      distinctZoom$,
      windowResize$
    )
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.zoomScale = this.zoomScale$.getValue();
        // ScaleFactor is defaults to .24 because thats the scale from 300 ppi to 72 ppi
        this.scaleFactor = this.scaleFactor$.getValue();
        this.clearSelectedAnnotation();
        this.createCanvasSubscription?.unsubscribe();
        this.createCanvasSubscription = this.createCanvas(true).subscribe(
          () => {
            this.attachCanvasListeners();
            this.redrawAnnotations(true);
          }
        );
      });
  }

  /** Returns if annotations wrapper exists. */
  doesWrapperExist(): boolean {
    if (!this.pageElement) return false;
    const wrapper = this.pageElement.querySelector(
      '#annotations_wrapper_page_' + this.pageNumber
    );
    return !!wrapper;
  }

  /** Get the current annotation Canvas. */
  getAnnotationsCanvas(): HTMLCanvasElement {
    const canvas = this.pageElement.querySelector(
      '#document_annotation_page_' + this.pageNumber
    ) as HTMLCanvasElement;
    return canvas;
  }

  /**
   * Clears the annotation canvas.
   */
  private clearCanvas(): void {
    if (!this.annotationDrawingCanvas || !this.documentAnnotationContext)
      return;

    const pixelRatio = window.devicePixelRatio || 1;
    this.documentAnnotationContext.save();
    this.documentAnnotationContext.setTransform(1, 0, 0, 1, 0, 0);
    this.documentAnnotationContext.clearRect(
      0,
      0,
      this.annotationDrawingCanvas.width,
      this.annotationDrawingCanvas.height
    );
    this.documentAnnotationContext.restore();
    this.documentAnnotationContext.setTransform(
      pixelRatio,
      0,
      0,
      pixelRatio,
      0,
      0
    );
  }

  /**
   * Reloads Canvas, Listeners, and annotations.
   */
  reloadDrawingCanvas(): void {
    // Clear selected annotation.
    this.selectedAnnotation = undefined;
    // Recreate the canvas because the previous one was detroyed on rotation.
    this.createCanvasSubscription?.unsubscribe();
    this.createCanvasSubscription = this.createCanvas().subscribe(() => {
      // Reattach listeners.
      this.attachCanvasListeners();
      // Draw the annotations on the canvas.
      this.redrawAnnotations();
    });
  }

  /**
   * Create the canvas for drawing annotations.
   */
  createCanvas(resizeObserver: boolean = false): Observable<HTMLCanvasElement> {
    if (!this.pageElement) return of();
    // Don't need to create anything if it's already there.
    if (!this.doesWrapperExist()) {
      // Observer returns when the canvas object exists.
      const observer = resizeObserver
        ? this.resizeObservePdfCanvas()
        : this.observePdfCanvas();
      return observer.pipe(
        map((element: HTMLCanvasElement) => {
          if (
            !this.pageElement.contains(this.annotationWrapper) ||
            !this.annotationWrapper.contains(this.annotationDrawingCanvas)
          ) {
            this.annotationWrapper = document.createElement('div');
            this.annotationWrapper.className = 'annotationWrapper';
            this.annotationWrapper.id =
              'annotations_wrapper_page_' + this.pageNumber;
            this.pageElement.append(this.annotationWrapper);
            // Create canvas element for drawing.
            this.annotationDrawingCanvas = document.createElement('canvas');
            this.annotationDrawingCanvas.id =
              'document_annotation_page_' + this.pageNumber;

            // New annotation canvas should sit on top of the wrapper and match it's size.
            this.annotationDrawingCanvas.height = element.height || 0;
            this.annotationDrawingCanvas.width = element.width || 0;
            this.annotationDrawingCanvas.style.position = 'absolute';
            this.annotationDrawingCanvas.style.top = '0px';
            this.annotationDrawingCanvas.style.left = '0px';
            this.annotationDrawingCanvas.style.zIndex = '2';
            // Style height and width are needed for dealing with device pixel ratio.
            this.annotationDrawingCanvas.style.width = '100%';
            this.annotationDrawingCanvas.style.height = '100%';
            this.annotationDrawingCanvas.tabIndex = 0;
            // Append the new annotation canvas to the annotation wrapper.
            this.annotationWrapper.append(this.annotationDrawingCanvas);
            // Create a context in which to draw with.
            this.documentAnnotationContext =
              this.annotationDrawingCanvas.getContext(
                '2d'
              ) as CanvasRenderingContext2D;

            // Scale the canvas to match device pixel ratio.
            this.documentAnnotationContext.scale(
              window.devicePixelRatio,
              window.devicePixelRatio
            );

            // Restore pointer state
            this.setPointerEvents(this.pointerEventsEnabled());
          }

          return this.annotationDrawingCanvas;
        })
      );
    } else {
      this.annotationDrawingCanvas = this.getAnnotationsCanvas();
      // Create a context in which to draw with.
      this.documentAnnotationContext = this.annotationDrawingCanvas.getContext(
        '2d'
      ) as CanvasRenderingContext2D;

      // Restore pointer state
      this.setPointerEvents(this.pointerEventsEnabled());
      return of(this.annotationDrawingCanvas);
    }
  }

  /**
   * Observer that checks for the page canvas object base on resize that occur in the DOM.
   * We need this to get page size. And attach the annotation canvas.
   * @returns - HtmlCanvasElement
   */
  private resizeObservePdfCanvas(): Observable<HTMLCanvasElement> {
    return new Observable((subscriber) => {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const element = entry.target.querySelector(
            '.canvasWrapper canvas'
          ) as HTMLCanvasElement;

          if (element && this.pageElement.contains(element)) {
            subscriber.next(element); // Emit the element
            resizeObserver.disconnect(); // Stop observing
          }
        }
      });

      // Start observing the wrapper
      resizeObserver.observe(this.pageElement);

      // Cleanup when unsubscribed
      return () => resizeObserver.disconnect();
    });
  }

  /**
   * Observer that checks for the page canvas object base on changes that occur in the DOM.
   * We need this to get page size. And attach the annotation canvas.
   * @returns - HtmlCanvasElement
   */
  private observePdfCanvas(): Observable<HTMLCanvasElement> {
    return new Observable((subscriber) => {
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const element = this.pageElement.querySelector(
              '.canvasWrapper canvas'
            ) as HTMLCanvasElement; /** * requestAnimationFrame ensures the dom rendering is complete before we check * if the element exits etc... */
            requestAnimationFrame(() => {
              if (this.pageElement.contains(element)) {
                subscriber.next(element); // Emit the detected element
                observer.disconnect();
                // Stop observing
                return;
              }
            });
          }
        }
      });
      observer.observe(this.pageElement, { childList: true, subtree: true });
      // Cleanup logic when subscription is unsubscribed
      return () => observer.disconnect();
    });
  }

  /**
   * Rotate the annotation.
   * @param anno - Annotation.
   * @param mousePos - Mouse position x and y.
   */
  private dragHandleRotation(
    anno: DocumentAnnotation,
    mousePos: { x: number; y: number }
  ): void {
    assertSupportedAnnotation(anno);
    anno.rotation += this.getAngleBetweenPoints(
      this.startMousePos,
      { x: anno.x, y: anno.y },
      mousePos
    );
    this.updateRotation(anno);
    this.redrawAnnotations();
    this.startMousePos = mousePos;
  }

  /**
   * Returns the angle of angle point.
   * @param startPoint - Starting point.
   * @param anglePoint - Angle we want to get the point of.
   * @param endPoint - End point.
   * @returns - Angle in radians.
   */
  private getAngleBetweenPoints(
    startPoint: Point,
    anglePoint: Point,
    endPoint: Point
  ) {
    const angle1 = Math.atan2(
      startPoint.y - anglePoint.y,
      startPoint.x - anglePoint.x
    );
    const angle2 = Math.atan2(
      endPoint.y - anglePoint.y,
      endPoint.x - anglePoint.x
    );

    let angleDifference = angle2 - angle1;

    // Normalize to [-π, π] to prevent jumps
    if (angleDifference > Math.PI) angleDifference -= 2 * Math.PI;
    if (angleDifference < -Math.PI) angleDifference += 2 * Math.PI;

    // 53.7 seems to keep the mouse and handle in line.
    return angleDifference * 53.7;
  }

  /**
   * Determines if the passed annotation is rotatable.
   *
   * @param annotation - Annotation.
   * @returns - Whether the annotation can be rotated.
   */
  isRotatable(annotation: DocumentAnnotation): boolean {
    return (
      annotation instanceof RectangleAnnotation ||
      annotation instanceof TextAnnotation ||
      annotation instanceof StampAnnotation ||
      annotation instanceof RectangleAnnotation ||
      annotation instanceof EllipseAnnotation ||
      annotation instanceof PathAnnotation
    );
  }

  /**
   * Returns the index of the passed annotation.
   *
   * @param annotation - Annotation.
   * @returns - Index(number).
   */
  private getAnnotationIndex(annotation: DocumentAnnotation): number {
    assertSupportedAnnotation(annotation);
    return this.annotations.findIndex((anno) => {
      assertSupportedAnnotation(anno);
      return anno.uuid === annotation.uuid;
    });
  }

  /**
   * Move the annotation around in the x and y plane.
   * @param e - Mouse Event.
   * @param anno - Annotation.
   * @param mousePos - Mouse Postion.
   */
  private dragAnnotation(
    e: MouseEvent,
    anno: DocumentAnnotation,
    mousePos: { x: number; y: number }
  ): void {
    assertSupportedAnnotation(anno);
    assertExists(this.selectedAnnotation);
    const dx = mousePos.x - this.startMousePos.x;
    const dy = mousePos.y - this.startMousePos.y;
    const index = this.getAnnotationIndex(anno);
    anno.x += dx;
    anno.y += dy;
    this.annotations[index].x += dx / (this.scaleFactor * this.zoomScale);
    this.annotations[index].y += dy / (this.scaleFactor * this.zoomScale);

    if (
      anno instanceof LineAnnotation &&
      this.annotations[index] instanceof LineAnnotation
    ) {
      anno.end.x += dx;
      anno.end.y += dy;
      this.annotations[index].end.x += dx / (this.scaleFactor * this.zoomScale);
      this.annotations[index].end.y += dy / (this.scaleFactor * this.zoomScale);
    }

    this.redrawAnnotations();
    this.startMousePos = mousePos;
    this.setMouseCursor(e);
  }

  /**
   * Resize annotation by dragging the handles.
   *
   * @param e - Mouse Event.
   * @param mousePos - Mouse Position.
   */
  private dragResizeAnnotation(
    e: MouseEvent,
    mousePos: { x: number; y: number }
  ): void {
    const handlePositions = this.getHandlePositions();
    if (!handlePositions) return;
    switch (this.currentHandle) {
      case 0: // Top-left handle
        this.resizeAnnotation(e, -1, -1);
        break;
      case 1: // Top-right handle
        this.resizeAnnotation(e, 1, -1);
        break;
      case 2: // Bottom-left handle
        this.resizeAnnotation(e, -1, 1);
        break;
      case 3: // Bottom-right handle
        this.resizeAnnotation(e, 1, 1);
        break;
    }
    this.redrawAnnotations();
    this.startMousePos = mousePos;
  }

  /**
   * Set dirty status.
   *
   * @param isDirty - Is dirty.
   */
  setDirtyStatus(isDirty: boolean) {
    this.dirty$.next(isDirty);
  }

  /**
   * Attached mouse leave listener to the canvas.
   */
  private addMouseLeaveListener(): void {
    this.annotationDrawingCanvas.addEventListener('mouseleave', (e) => {
      this.stopMouseActions(e);
    });
  }

  /**
   * Attaches the mouse move listener to the canvas.
   */
  private addMouseMoveListener(): void {
    this.annotationDrawingCanvas.addEventListener('mousemove', (e) => {
      if (!this.selectedAnnotation) return;
      const anno = this.selectedAnnotation;
      const mousePos = this.getMousePos(e);
      // Dragging handle for rotation.
      if (this.draggingHandle) {
        this.dragHandleRotation(anno, mousePos);
        this.setDirtyStatus(true);
      }
      // Dragging annotation.
      else if (this.dragging) {
        this.dragAnnotation(e, anno, mousePos);
        this.setDirtyStatus(true);
      }
      // Resizing annotation by handles.
      else if (this.resizing) {
        this.dragResizeAnnotation(e, mousePos);
        this.setDirtyStatus(true);
      }
      this.setMouseCursor(e);
      this.annotationChanged$.next();
    });
  }

  /**
   * Add a double click listener to the canvas.
   */
  private addMouseDoubleClickListener = (): void => {
    this.annotationDrawingCanvas.addEventListener('dblclick', (e) => {
      if (!this.selectedAnnotation) return;
      if (!(this.selectedAnnotation instanceof ImageAnnotation)) {
        this.showProperties();
      }
    });
  };

  /**
   * Add a key down listener to the canvas.
   */
  private addKeyDownListener(): void {
    this.annotationDrawingCanvas.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {
        if (event.key === 'Delete') {
          this.deleteSelectedAnnotation();
          this.annotationChanged$.next();
        }
      }
    );
  }

  /**
   * Attaches the mouse down listener to the canvas.
   */
  private addMouseDownListener(): void {
    // Mouse down event
    this.annotationDrawingCanvas.addEventListener('mousedown', (e) => {
      this.destroyMenu();
      const mousePos = this.getMousePos(e);

      if (this.selectedAnnotation) {
        const handlePositions = this.getHandlePositions();
        const cornerHandles = [...handlePositions.slice(0, -1)];
        assertExists(cornerHandles);
        for (let i = 0; i < cornerHandles.length; i++) {
          if (this.isMouseOnHandle(mousePos, cornerHandles[i])) {
            this.resizing = true;
            this.startMousePos = mousePos;
            this.currentHandle = i;
            return;
          }
        }
        if (this.isMouseOnHandle(mousePos, handlePositions[4])) {
          this.draggingHandle = true;
          this.startMousePos = mousePos;
          this.currentHandle = 4;
          return;
        }
      }

      // Make sure annotation is selected.
      const currentSelection = this.selectedAnnotation;
      this.selectedAnnotation = this.isMouseOverAnnotation(
        mousePos.x,
        mousePos.y
      );
      if (!this.isSameAnnotation(currentSelection)) {
        this.redrawAnnotations();
        return;
      }
      assertExists(this.selectedAnnotation);
      // Check if mouse is over any handles

      this.dragging = true;
      this.startMousePos = mousePos;
    });
  }

  /**
   * Adds the mouse up event listener.
   */
  private addMouseUpEventListener(): void {
    // Mouse up event
    this.annotationDrawingCanvas.addEventListener(
      'mouseup',
      (e: MouseEvent) => {
        this.stopMouseActions(e);
      }
    );
  }

  /**
   * Stops all mouse dragging, rotating, moving actions.
   * @param e - Mouse Event
   */
  private stopMouseActions(event: MouseEvent): void {
    if (this.resizing) {
      this.adjustPathsAfterResize();
      this.redrawAnnotations(true);
    }
    this.dragging = false;
    this.draggingHandle = false;
    this.resizing = false;
    this.currentHandle = null;
    this.redrawThumbnailAnnotations(true);
    this.setMouseCursor(event);
  }

  /**
   * We have to adjust the path, x, and y after resizing if the values went into the negatives.
   * We adjust all the points to not be negative and then x and y in relation to the points.
   */
  private adjustPathsAfterResize() {
    const annotation = this.selectedAnnotation;
    assertSupportedAnnotation(annotation);
    const index = this.getAnnotationIndex(annotation);
    if (
      annotation instanceof PathAnnotation &&
      this.annotations[index] instanceof PathAnnotation
    ) {
      const minX = this.getFreehandX(annotation.pathPoints);
      const minY = this.getFreehandY(annotation.pathPoints);
      let points = annotation.pathPoints;
      for (let i = 0, c = points.length; i < c; i = i + 2) {
        if (minX < 0) {
          points[i] = points[i] + Math.abs(minX);
        }
        if (minY < 0) {
          points[i + 1] = points[i + 1] + Math.abs(minY);
        }
      }
      this.annotations[index].pathPoints = points.map((point) => {
        return point / (this.zoomScale * this.scaleFactor);
      });
      if (minX < 0) {
        this.annotations[index].x =
          (annotation.x - Math.abs(minX) / 2) /
          (this.scaleFactor * this.zoomScale);
      }
      if (minY < 0) {
        this.annotations[index].y =
          (annotation.y - Math.abs(minY) / 2) /
          (this.scaleFactor * this.zoomScale);
      }
    }
  }

  /**
   * Adds the context menu event listener.
   */
  private addContextMenuEventListener(): void {
    this.annotationDrawingCanvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const mousePos = this.getMousePos(e);
      // Make sure annotation is selected.
      const currentSelection = this.selectedAnnotation;
      this.selectedAnnotation = this.isMouseOverAnnotation(
        mousePos.x,
        mousePos.y
      );
      let menuItems: { label: string; action: () => void }[] = [];
      if (this.isSameAnnotation(currentSelection)) {
        // Define menu items
        menuItems = [
          {
            label: 'Delete',
            action: () => {
              this.deleteSelectedAnnotation();
              this.annotationChanged$.next();
            },
          },
          {
            label: 'Properties',
            action: () => this.showProperties(),
          },
          {
            label: 'Duplicate',
            action: () => {
              this.duplicateAnnotation(e);
            },
          },
          {
            label: 'Cut',
            action: () => {
              this.cutAnnotation(e);
            },
          },
          {
            label: 'Copy',
            action: () => {
              this.copyAnnotation(e);
            },
          },
        ];

        // Images don't have properties.
        if (currentSelection instanceof ImageAnnotation) {
          menuItems = menuItems.filter((item) => item.label !== 'Properties');
          if (this.modifyDocumentPermissions) {
            menuItems.unshift({
              label: 'Burn',
              action: () => this.toggleBurn(),
            });
          }
        }
      }

      if (this.copiedAnnototaion) {
        menuItems.push({
          label: 'Paste',
          action: () => this.pasteAnnotation(e),
        });
      }

      // Initialize and show the context menu
      this.createContextMenu(menuItems);
      this.showContextMenu(mousePos.x, mousePos.y);
    });
  }

  /**
   * Cut the annotation, copying it into a variable and removing it from the annotations list.
   * @param event - Mouse Event
   */
  private cutAnnotation(event: MouseEvent) {
    this.copyAnnotation(event);
    // Delete the cut annotation from the list.
    this.deleteSelectedAnnotation();
  }

  /**
   * Copy the selected annotation, into the copied annotation variable.
   * @param event - Mouse Event
   */
  private copyAnnotation(event: MouseEvent) {
    assertExists(this.selectedAnnotation);
    const index = this.getAnnotationIndex(this.selectedAnnotation);
    // Copy the annotation into the copied variable.
    this.copiedAnnototaion = this.cloneAnnotation(this.annotations[index]);
  }

  /**
   * Pastes a copied or cut annotation to wherever the mouse is.
   * @param event
   */
  private pasteAnnotation(event: MouseEvent) {
    const mousePos = this.getMousePos(event);
    assertExists(this.copiedAnnototaion);
    const annotation = this.cloneAnnotation(this.copiedAnnototaion);

    const scale = this.scaleFactor * this.zoomScale;

    const rotation = (annotation.rotation * Math.PI) / 180;
    const halfWidth = annotation.width / 2;
    const halfHeight = annotation.height / 2;

    // Correct center for rotations
    annotation.x =
      mousePos.x / (this.scaleFactor * this.zoomScale) -
      halfWidth * Math.cos(rotation) +
      halfHeight * Math.sin(rotation);
    annotation.y =
      mousePos.y / (this.scaleFactor * this.zoomScale) -
      halfWidth * Math.sin(rotation) -
      halfHeight * Math.cos(rotation);

    if (annotation instanceof LineAnnotation) {
      annotation.end.x = annotation.x + annotation.width;
      annotation.end.y = annotation.y + annotation.height;
    }
    annotation.page = this.getPageNumberIndex();
    annotation.uuid = uuid();
    this.addAnnotations(annotation);
    this.setDirtyStatus(true);
    this.redrawAnnotations(true);
    this.annotationChanged$.next();
  }

  /**
   * Attach canvas listeners.
   */
  attachCanvasListeners() {
    if (this.annotationPermissions && this.annotationsSupported) {
      assertExists(this.annotationDrawingCanvas);
      assertExists(
        (this.annotationDrawingCanvas as HTMLCanvasElement & ZoneEventTarget)
          .removeAllListeners
      );

      (
        this.annotationDrawingCanvas as HTMLCanvasElement & ZoneEventTarget
      ).removeAllListeners();
      this.addMouseMoveListener();
      this.addMouseLeaveListener();
      this.addKeyDownListener();
      this.addMouseDownListener();
      this.addMouseDoubleClickListener();
      this.addMouseUpEventListener();
      this.addContextMenuEventListener();
    }
  }

  /**
   * Returns the annotation if it has been clicked.
   * @param x - X coordinate.
   * @param y - Y coordinate.
   * @returns - Document Annotation.
   */
  isMouseOverAnnotation = (
    x: number,
    y: number
  ): DocumentAnnotation | undefined => {
    const annotationsList =
      this.cachedAnnotations ||
      this.transformedAnnotations(this.zoomScale, this.scaleFactor);
    this.cachedAnnotations = annotationsList;
    // Reverse so we recognize the annotations by how they are stacked correctly.
    // Make a copy so the actual list doesn't get reversed.
    return [...annotationsList].reverse().find((anno: DocumentAnnotation) => {
      assertSupportedAnnotation(anno);

      const halfWidth = Math.abs(anno.width) / 2 + this.handleSize;
      const halfHeight = Math.abs(anno.height) / 2 + this.handleSize;

      const rotation = (anno.rotation * Math.PI) / 180;
      const sin = Math.sin(rotation);
      const cos = Math.cos(rotation);

      const localX = (x - anno.x) * cos + (y - anno.y) * sin;
      const localY = -(x - anno.x) * sin + (y - anno.y) * cos;

      return (
        localX > -halfWidth &&
        localX < halfWidth &&
        localY > -halfHeight &&
        localY < halfHeight &&
        anno.page === this.getPageNumberIndex()
      );
    });
  };

  /**
   * Sets mouse cursor.
   *
   * @param e - Mouse Event
   */
  setMouseCursor = (e: MouseEvent) => {
    const mousePos = this.getMousePos(e);
    const currentAnnotation = this.isMouseOverAnnotation(
      mousePos.x,
      mousePos.y
    );
    this.annotationDrawingCanvas.style.cursor = 'default';
    if (
      currentAnnotation &&
      this.selectedAnnotation &&
      this.isSameAnnotation(currentAnnotation)
    ) {
      this.annotationDrawingCanvas.style.cursor = 'move';
    }

    const handlePositions = this.getHandlePositions();
    for (const handle of handlePositions) {
      if (this.isMouseOnHandle(mousePos, handle)) {
        this.annotationDrawingCanvas.style.cursor = 'pointer';
        return;
      }
    }
  };

  /**
   * Returns if it is the same annotation
   */
  private isSameAnnotation = (passedAnno: DocumentAnnotation | undefined) => {
    // It's okay for it not to exist so we don't want errors.
    if (!passedAnno || !this.selectedAnnotation) return;
    assertSupportedAnnotation(this.selectedAnnotation);
    assertSupportedAnnotation(passedAnno);
    return passedAnno.uuid === this.selectedAnnotation.uuid;
  };

  /**
   * Scales path annotation points when resizing a path annotation
   * @param origAnno - Annotation before resizing.
   * @param updatedAnno - Annotaion after resizing.
   */
  private updatePath(
    updatedAnno: PathAnnotation,
    origWidth: number,
    origHeight: number
  ) {
    // Need to scale path annotations for resizing to adjust nicel.y
    const scaleX = updatedAnno.width / origWidth;
    const scaleY = updatedAnno.height / origHeight;
    let points = updatedAnno.pathPoints;
    for (let i = 0, c = points.length; i < c; i = i + 2) {
      points[i] = points[i] * scaleX;
      points[i + 1] = points[i + 1] * scaleY;
    }
    const index = this.getAnnotationIndex(updatedAnno);
    (this.annotations[index] as PathAnnotation).pathPoints = points.map(
      (point, i) => {
        return i % 2 === 0
          ? point / (this.zoomScale * this.scaleFactor)
          : point / (this.zoomScale * this.scaleFactor);
      }
    );
  }

  /**
   * Sets the annotation resize via mouse coordinates.
   */
  private resizeAnnotation(e: MouseEvent, scaleX: number, scaleY: number) {
    assertSupportedAnnotation(this.selectedAnnotation);

    // Reverse the annotation so we can manipulate it upright.
    const originalWidth = this.selectedAnnotation.width;
    const originalHeight = this.selectedAnnotation.height;

    // Get the index of the currently selected annotation.
    const annoIndex = this.getAnnotationIndex(this.selectedAnnotation);
    const mousePos = this.getMousePos(e);

    // Get the x and y after rotation.
    const [rotatedDX, rotatedDY] = this.getRotatedDeltas(
      this.selectedAnnotation,
      mousePos,
      this.startMousePos
    );

    // Update the annotation dimentions(width and height).
    this.updateAnnotationDimensions(
      this.selectedAnnotation,
      rotatedDX,
      rotatedDY,
      scaleX,
      scaleY,
      annoIndex
    );

    // Update the annotation positions(x and y).
    this.updateAnnotationPosition(
      this.selectedAnnotation,
      rotatedDX,
      rotatedDY,
      annoIndex
    );

    if (this.selectedAnnotation instanceof PathAnnotation) {
      this.updatePath(this.selectedAnnotation, originalWidth, originalHeight);
    }
  }

  /**
   * Returns the x and y of the annotation after rotation.
   *
   * @param anno - Annotation.
   * @param mousePos - Mouse Position.
   * @param startMousePos - Starting Mouse Position.
   * @returns - x and y.
   */
  private getRotatedDeltas(
    anno: DocumentAnnotation,
    mousePos: { x: number; y: number },
    startMousePos: { x: number; y: number }
  ): [number, number] {
    assertSupportedAnnotation(anno);
    let dx = mousePos.x - startMousePos.x;
    let dy = mousePos.y - startMousePos.y;
    let sin = Math.sin((anno.rotation * Math.PI) / 180);
    let cos = Math.cos((anno.rotation * Math.PI) / 180);

    let rotatedDX = dx * cos + -dy * sin;
    let rotatedDY = -dx * sin + -dy * cos;

    [rotatedDX, rotatedDY] = this.rotateDeltas(dx, dy, cos, sin, 1, 1);

    return [rotatedDX, rotatedDY];
  }

  /**
   * Returns the rotated deltas of x and y.
   *
   * @param dx - Change in mouse x position.
   * @param dy - Change in mouse y position.
   * @param cos - Cosine of the annotation angle.
   * @param sin - Sine of the annotation angle.
   * @param factorX - Factor of x. Use 1 and -1.
   * @param factorY - Factor of y. Use 1 and -1.
   * @param annoRotation - Annotation rotation.
   * @returns Rotated x and y
   */
  private rotateDeltas(
    dx: number,
    dy: number,
    cos: number,
    sin: number,
    factorX: number,
    factorY: number,
    annoRotation: number = 0
  ): [number, number] {
    if (annoRotation > 0) {
      const oldDY = dy;
      dy = dx;
      dx = oldDY * -1;
    }
    const rotatedDX = factorX * (dx * cos + dy * sin);
    const rotatedDY = factorY * (-dx * sin + dy * cos);
    return [rotatedDX, rotatedDY];
  }

  /**
   * Update the annotations height and width;
   *
   * @param anno - Annotation.
   * @param rotatedDX - Rotated x.
   * @param rotatedDY - Rotated y.
   * @param scaleX - Scale x.
   * @param scaleY - Scale y.
   * @param index - Index of the annotation in the annotations list.
   */
  updateAnnotationDimensions(
    anno: DocumentAnnotation,
    rotatedDX: number,
    rotatedDY: number,
    scaleX: number,
    scaleY: number,
    index: number
  ) {
    assertSupportedAnnotation(anno);
    anno.width += rotatedDX * scaleX;
    anno.height += rotatedDY * scaleY;
    (this.annotations[index] as RectangleAnnotation).height =
      anno.height / (this.scaleFactor * this.zoomScale);
    (this.annotations[index] as RectangleAnnotation).width =
      anno.width / (this.scaleFactor * this.zoomScale);

    if (
      anno instanceof EllipseAnnotation &&
      this.annotations[index] instanceof EllipseAnnotation
    ) {
      this.annotations[index].xScale = this.annotations[index].width / 2;
      this.annotations[index].yScale = this.annotations[index].height / 2;
    }
  }

  /**
   * Update the rectangles position.
   *
   * @param anno - Annotation.
   * @param rotatedDX - Rotated x.
   * @param rotatedDY - Rotated y.
   * @param index - Index of the annotation in the annotations list.
   */
  updateAnnotationPosition(
    anno: DocumentAnnotation,
    rotatedDX: number,
    rotatedDY: number,
    index: number
  ) {
    assertSupportedAnnotation(anno);
    const rotation = (anno.rotation * Math.PI) / 180;
    anno.x +=
      (rotatedDX / 2) * Math.cos(rotation) -
      (rotatedDY / 2) * Math.sin(rotation);
    anno.y +=
      (rotatedDX / 2) * Math.sin(rotation) +
      (rotatedDY / 2) * Math.cos(rotation);

    let dx = -anno.width / 2;
    let dy = -anno.height / 2;
    const rotatedX =
      anno.x + (dx * Math.cos(rotation) - dy * Math.sin(rotation));
    const rotatedY =
      anno.y + (dx * Math.sin(rotation) + dy * Math.cos(rotation));
    this.annotations[index].x = rotatedX / (this.scaleFactor * this.zoomScale);
    this.annotations[index].y = rotatedY / (this.scaleFactor * this.zoomScale);

    /** Adjust end point for Line Annotations. */
    if (
      anno instanceof LineAnnotation &&
      this.annotations[index] instanceof LineAnnotation
    ) {
      this.annotations[index].end.x =
        (rotatedX + anno.width) / (this.scaleFactor * this.zoomScale);
      this.annotations[index].end.y =
        (rotatedY + anno.height) / (this.scaleFactor * this.zoomScale);
    }
  }

  /**
   * Update annotation rotation, x and y.
   *
   * @param annotation - Annotation.
   */
  updateRotation(annotation: DocumentAnnotation) {
    let anno = this.cloneAnnotation(annotation);
    assertSupportedAnnotation(anno);
    let halfWidth = anno.width / 2;
    let halfHeight = anno.height / 2;
    const rotation = (anno.rotation * Math.PI) / 180;
    // Calculate the rotated top-left corner
    let rotatedX =
      anno.x - halfWidth * Math.cos(rotation) + halfHeight * Math.sin(rotation);
    let rotatedY =
      anno.y - halfWidth * Math.sin(rotation) - halfHeight * Math.cos(rotation);
    const annoIndex = this.getAnnotationIndex(anno);
    this.annotations[annoIndex].rotation =
      ((((rotation * 180) / Math.PI) % 360) + 360) % 360;
    this.annotations[annoIndex].x =
      rotatedX / (this.scaleFactor * this.zoomScale);
    this.annotations[annoIndex].y =
      rotatedY / (this.scaleFactor * this.zoomScale);

    /** Adjust end point for Line Annotations. */
    if (
      anno instanceof LineAnnotation &&
      this.annotations[annoIndex] instanceof LineAnnotation
    ) {
      this.annotations[annoIndex].end.x =
        (rotatedX + anno.width) / (this.scaleFactor * this.zoomScale);
      this.annotations[annoIndex].end.y =
        (rotatedY + anno.height) / (this.scaleFactor * this.zoomScale);
    }
  }

  /**
   * Rotates the passed annotation based on the page rotation.
   *
   * @param transformedAnno - Annotation.
   * @param canvasWidth
   * @param canvasHeight
   * @returns - Rotated annotation.
   */
  private rotateTransformedAnnotation(
    transformedAnno: DocumentAnnotation,
    canvasWidth: number,
    canvasHeight: number
  ) {
    assertSupportedAnnotation(transformedAnno);
    return transformedAnno;
  }

  /**
   * Creates context menu.
   *
   * @param items - Items to add to the context menu.
   */
  private createContextMenu(items: { label: string; action: () => void }[]) {
    // If a menu already exists, remove it
    if (this.menuElement) {
      this.destroyMenu();
    }

    // Create the menu container
    this.menuElement = document.createElement('div');
    this.menuElement.style.position = 'absolute';
    this.menuElement.style.background = '#fff';
    //this.menuElement.style.border = '1px solid #ccc';
    this.menuElement.style.zIndex = '1000';
    this.menuElement.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
    this.menuElement.style.borderRadius = '4px';
    this.menuElement.style.paddingTop = '8px';
    this.menuElement.style.paddingBottom = '8px';
    this.menuElement.style.zIndex = '1000';

    // Populate menu with items
    for (const item of items) {
      const button = document.createElement('button');
      button.textContent = item.label;
      button.style.display = 'block';
      button.style.width = '100%';
      button.style.border = 'none';
      button.style.background = 'transparent';
      button.style.textAlign = 'left';
      button.style.cursor = 'pointer';
      button.style.padding = '8px 16px 8px 16px';
      button.style.transition = 'background-color 0.3s ease';
      button.style.font = 'Roboto, sans-serif';
      button.style.fontSize = '16px';
      button.style.letterSpacing = '0.03125em';
      button.style.lineHeight = '24px';

      if (this.selectedAnnotation?.burn && item.label === 'Burn') {
        button.style.color = 'red';
      }

      button.addEventListener('mouseover', () => {
        // Change to a lighter color on hover
        button.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
      });

      button.addEventListener('mouseout', () => {
        // Revert to the default color
        button.style.backgroundColor = '#fff';
      });

      button.addEventListener('click', () => {
        item.action();
        // Close menu after an action
        this.destroyMenu();
      });

      this.menuElement.appendChild(button);
    }

    // Append the menu to the document body
    this.pageElement.appendChild(this.menuElement);
  }

  /**
   * Show the context menu at the passed coordinates.
   * @param x - X coordinate.
   * @param y - Y coordinate.
   */
  private showContextMenu(x: number, y: number) {
    if (this.menuElement) {
      this.menuElement.style.left = `${x}px`;
      this.menuElement.style.top = `${y}px`;
      this.menuElement.style.display = 'block';
    }
  }

  /**
   * Destroy the context menu.
   */
  private destroyMenu() {
    if (this.menuElement) {
      this.menuElement.remove();
      this.menuElement = null as unknown as HTMLElement; // Reset the reference
    }
  }

  /**
   * Delete the selected annotation.
   */
  private deleteSelectedAnnotation() {
    assertExists(
      this.selectedAnnotation,
      'Annotation must be selected to delete.'
    );
    const index = this.getAnnotationIndex(this.selectedAnnotation);
    this.selectedAnnotation = undefined;
    this.annotationDrawingCanvas.style.cursor = 'default';
    this.logger.debug('Deleting annotation: ', this.annotations[index]);
    const annotations = [...this.annotations];
    annotations.splice(index, 1);
    this.annotations = annotations;
    this.redrawAnnotations(true);
    this.dirty$.next(true);
  }

  /**
   * Delete the passed annotation.
   * @param annotation - Annotation.
   */
  private deleteAnnotation(annotation: DocumentAnnotation) {
    assertExists(annotation, 'Annotation must be passed to delete.');
    const index = this.getAnnotationIndex(annotation);
    this.logger.debug('Deleting annotation: ', annotation);
    const annotations = [...this.annotations];
    annotations.splice(index, 1);
    this.annotations = annotations;
    this.redrawAnnotations(true);
    this.dirty$.next(true);
  }

  /**
   * Rotate pages by indexes.
   * @param pageIndex - Page to rotate.
   * @param rotationAmount- Amount to rotate the page.
   */
  rotateAnnotationByPageIndex(indexes: number[], rotationAmount: number) {
    const annotations = [...this.annotations];
    for (let annotation of annotations) {
      if (indexes.includes(annotation.page)) {
        this.rotateAnnotation(
          annotation,
          rotationAmount,
          this.zoomScale * this.scaleFactor
        );
      }
    }
    this.annotations$.next(annotations);
    this.setDirtyStatus(true);
  }

  /**
   * Rotate the passed annotation by the passed amount in degrees.
   * @param annotation - Annotation to be rotated.
   * @param rotationAmount - Rotation amount in degrees.
   * @param scale - Current annotation scale.
   * @param flipCanvas - Flips the height and the width for when the rotation takes place after the canvas has been rotated.
   */
  private rotateAnnotation(
    annotation: DocumentAnnotation,
    rotationAmount: number,
    scale: number,
    flipCanvas: boolean = false
  ) {
    assertSupportedAnnotation(annotation);

    // Scaling the original down here so we know the scaling at the begining and end.
    const scaledAnno = {
      x: annotation.x * scale,
      y: annotation.y * scale,
      height: annotation.height * scale,
      width: annotation.width * scale,
      end: {
        x: 0,
        y: 0,
      },
    };

    // Set rotation and normalize to 360 using modulo arithmetic. We can ignore line annotations since we move all the points.
    if (!(annotation instanceof LineAnnotation)) {
      annotation.rotation = (annotation.rotation + rotationAmount) % 360;
    }

    // Convert degrees to radians.
    const radians = (rotationAmount * Math.PI) / 180;

    // Center coordinates.
    let centerX = this.annotationDrawingCanvas.width / 2;
    let centerY = this.annotationDrawingCanvas.height / 2;
    if (flipCanvas) {
      centerX = this.annotationDrawingCanvas.height / 2;
      centerY = this.annotationDrawingCanvas.width / 2;
    }

    // Coordinates relative to center.
    const relativeX = scaledAnno.x - centerX;
    const relativeY = scaledAnno.y - centerY;

    // Rotate.
    const x1 = relativeX * Math.cos(radians) - relativeY * Math.sin(radians);
    const y1 = relativeX * Math.sin(radians) + relativeY * Math.cos(radians);

    // Adjust coordinates based on center points after rotation.
    const newX = x1 + centerY;
    const newY = y1 + centerX;

    if (annotation instanceof LineAnnotation) {
      // Set scaled end x and end y
      scaledAnno.end.x = annotation.end.x * scale;
      scaledAnno.end.y = annotation.end.y * scale;

      // Axis in relation to center.
      const relativeEndX = scaledAnno.end.x - centerX;
      const relativeEndY = scaledAnno.end.y - centerY;

      // Rotate
      const endX1 =
        relativeEndX * Math.cos(radians) - relativeEndY * Math.sin(radians);
      const endY1 =
        relativeEndX * Math.sin(radians) + relativeEndY * Math.cos(radians);

      // Unrelative to center.
      const newEndX = endX1 + centerY;
      const newEndY = endY1 + centerX;

      annotation.end.x = newEndX / scale;
      annotation.end.y = newEndY / scale;
    }

    /**
     * I don't know why, but when comparing with the old viewer the x after conversion is always differs by
     * exactly 4.708516867. So I am adding it back here. Y value is always correct. Unscale by the same amount
     * we scaled by above.
     */
    annotation.x = newX / scale + 4.708516867;
    annotation.y = newY / scale;
  }

  /**
   * Updates observer so watchers can see the properties
   */
  private showProperties() {
    assertExists(
      this.selectedAnnotation,
      'Annotation must be selected to show properties.'
    );
    const index = this.getAnnotationIndex(this.selectedAnnotation);
    this.annotationProperties$.next(this.annotations[index]);
  }

  private toggleBurn() {
    assertExists(
      this.selectedAnnotation,
      'Annotation must be selected to toggle burn.'
    );
    this.selectedAnnotation.burn = !this.selectedAnnotation.burn;
    const index = this.getAnnotationIndex(this.selectedAnnotation);
    this.annotations[index].burn = !this.annotations[index].burn;
    this.setDirtyStatus(true);
  }

  thumbnailContainer: Element;

  /**
   * Listens for changes in the thumbnail containter and page count.
   */
  listenForThumbnailChanges() {
    let previousContainer: Element | null;
    let previousThumbRange: { start: number; end: number };

    // Ensure subscription is cleaned up before subscribing again
    this.thumbnailChangesSubscription?.unsubscribe();

    this.thumbnailChangesSubscription = combineLatest([
      this.thumbnailContainer$,
      this.thumbRange$,
      this.appQuery.renderAnnotationsInModifyThumbnailer$,
    ])
      .pipe(debounceTime(500))
      .subscribe(([container, thumbRange, renderThumbnailAnnotations]) => {
        if (!renderThumbnailAnnotations) {
          // Ensure any annotations that were rendered if the setting was on are removed.
          this.removeAllThumbnailAnnotations();
          return;
        }

        // Thumb range changes a ton so the following reduces it being called repeatedly.
        if (
          container === previousContainer &&
          JSON.stringify(thumbRange) === JSON.stringify(previousThumbRange)
        ) {
          return;
        }

        previousContainer = container;
        previousThumbRange = thumbRange;

        if (!container || !thumbRange) return;
        this.thumbnailContainer = container;
        this.thumbRange = thumbRange;
        this.redrawThumbnailAnnotations();
      });
  }

  /**
   * Redraw thumbnail annotations.
   */
  redrawThumbnailAnnotations(clearCache: boolean = false) {
    if (clearCache) this.lastThumbnailScale = 0;

    this.removeAllThumbnailAnnotations();

    // Let the cleanup code above run to remove annotations but don't
    // render them again if the setting is off.
    if (!this.appQuery.renderAnnotationsInModifyThumbnailer) return;

    for (var i = this.thumbRange.start, c = this.thumbRange.end; i <= c; ++i) {
      this.redrawThumbnailAnnotation(i);
    }
  }

  private removeAllThumbnailAnnotations() {
    const elements = document.querySelectorAll(
      '[id^="thumbnail_annotation_page_"]'
    );
    for (const element of elements) {
      element.remove();
    }
  }

  lastThumbnailScale: number = 0;
  cachedThumbnailAnnotations: DocumentAnnotations;

  /**
   * Redraws annotations on the passed page number.
   * @param pageNumber - Page Number.
   */
  redrawThumbnailAnnotation(pageNumber: number) {
    const canvasObservable = this.createThumbnailPageCanvas(pageNumber);
    canvasObservable.subscribe((canvas) => {
      if (!canvas) return;
      const context = canvas.getContext('2d') as CanvasRenderingContext2D;
      context.clearRect(0, 0, canvas.width, canvas.height);
      let scale = canvas.width / this.annotationDrawingCanvas.width;
      /**
       * If one page is rotated and the others are not the scaling gets weird.
       * by checking if the ratios between the thumbnails and main pages are different
       * we can determine if the ratio will be off and we have to use the main canvas height
       * for the scale instead of width. The ratios are not exactly identical so we use floor
       * to get the first 2 decimal places.
       */
      if (
        Math.floor(
          (this.annotationDrawingCanvas.width /
            this.annotationDrawingCanvas.height) *
            100
        ) !== Math.floor((canvas.width / canvas.height) * 100)
      ) {
        scale = canvas.width / this.annotationDrawingCanvas.height;
      }
      scale *= this.scaleFactor * this.zoomScale;
      let annotationsList: DocumentAnnotations;
      if (scale !== this.lastThumbnailScale) {
        annotationsList = this.transformedAnnotations(1, scale);
        this.cachedThumbnailAnnotations = annotationsList;
        this.lastThumbnailScale = scale;
      } else {
        annotationsList = this.cachedThumbnailAnnotations;
      }

      // Draw annotations.
      for (const anno of annotationsList) {
        if (!anno || anno.page !== pageNumber - 1) continue;
        this.drawThumbnailAnnotation(anno, context, scale);
      }
    });
  }

  /** Observes changes made on the thumbnail-page image element.
   *
   * @param pageNumber - Page to observe.
   * @returns - Observable thumbnail element.
   */
  private observeElementCreation(pageNumber: number): Observable<Element> {
    return new Observable((subscriber) => {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const element = entry.target.querySelector(
            '#thumbnail-page-' + pageNumber + ' img'
          ) as HTMLImageElement;
          if (element && element.src.indexOf('data:image') === 0) {
            subscriber.next(element); // Emit the detected element
            observer.disconnect(); // Stop observing
            return;
          }
        }
      });

      observer.observe(document.body);

      // Cleanup logic when subscription is unsubscribed
      return () => observer.disconnect();
    });
  }

  /**
   * Creates a thumbnail page canvas if it doesn't exist.
   *
   * @param pageNumber - Page Number.
   * @returns
   */
  createThumbnailPageCanvas(
    pageNumber: number
  ): Observable<HTMLCanvasElement | undefined> {
    const existingCanvas = this.thumbnailContainer?.querySelector(
      '#thumbnail_annotation_page_' + pageNumber
    ) as HTMLCanvasElement;
    if (existingCanvas) {
      return of(existingCanvas);
    }
    // Usage:
    const canvas = this.observeElementCreation(pageNumber).pipe(
      map((element: Element) => {
        const thumbnailImageContainer = this.thumbnailContainer?.querySelector(
          '#thumbnail-page-' + pageNumber + ' .thumbnail-image-container'
        );
        const thumbnailImage = this.thumbnailContainer?.querySelector(
          '#thumbnail-page-' + pageNumber + ' img'
        ) as HTMLImageElement;
        if (!thumbnailImageContainer || !thumbnailImage) return;
        const thumbnailAnnotationCanvas = document.createElement('canvas');
        thumbnailAnnotationCanvas.id =
          'thumbnail_annotation_page_' + pageNumber;
        // Height and width seems to be static.
        thumbnailAnnotationCanvas.height = thumbnailImage.height;
        thumbnailAnnotationCanvas.width = thumbnailImage.width;
        thumbnailAnnotationCanvas.style.position = 'absolute';
        thumbnailAnnotationCanvas.style.top =
          thumbnailImage.width > thumbnailImage.height ? '44px' : '0px';
        thumbnailAnnotationCanvas.style.left =
          thumbnailImage.width > thumbnailImage.height ? '0px' : '7px';
        thumbnailAnnotationCanvas.style.zIndex = '-1';
        thumbnailAnnotationCanvas.tabIndex = 0;
        if (!thumbnailImageContainer.contains(thumbnailAnnotationCanvas)) {
          // Append the new annotation canvas to the thumbnail container.
          thumbnailImageContainer.append(thumbnailAnnotationCanvas);
        }

        return thumbnailAnnotationCanvas as HTMLCanvasElement;
      })
    );
    return canvas;
  }

  /**
   * Draw an annotation on a thumbnail.
   * @param annotation - Annotation.
   * @param context - Context.
   * @param scale - Scale.
   */
  drawThumbnailAnnotation(
    annotation: DocumentAnnotation,
    context: CanvasRenderingContext2D,
    scale: number
  ) {
    if (annotation instanceof RectangleAnnotation) {
      this.drawRectangleAnnotation(annotation, context, scale);
    } else if (annotation instanceof StampAnnotation) {
      this.drawStampAnnotation(annotation, context, scale);
    } else if (annotation instanceof TextAnnotation) {
      this.drawTextAnnotation(annotation, context, scale);
    } else if (annotation instanceof PathAnnotation) {
      this.drawFreehandAnnotation(annotation, context, scale);
    } else if (annotation instanceof ImageAnnotation) {
      this.drawImageAnnotation(annotation, context);
    } else if (annotation instanceof LineAnnotation) {
      this.drawLineAnnotation(annotation, context, scale);
    }
  }

  /**
   * Returns annotations from the past indexes.
   *
   * @param indexes - Bursted pages index number.
   * @returns - DocumentAnnotations
   */
  getBurstedAnnotations(
    indexes: number[],
    deleteAnnotationsOnBurst: boolean = false
  ) {
    const annotations = this.annotations.map((a) => this.cloneAnnotation(a));
    if (deleteAnnotationsOnBurst) {
      this.deleteAnnotationsByPageIndexes(indexes);
    }
    return annotations.filter((a) => indexes.includes(a.page));
  }

  /**
   * Deletes the annotations by the passed index numbers.
   *
   * @param pagesToDelete - Array of indexes of pages to delete.
   */
  deleteAnnotationsByPageIndexes(pagesToDelete: number[]) {
    const annotationsToDelete = this.annotations.filter((annotation) =>
      pagesToDelete.includes(annotation.page)
    );
    for (const annotation of annotationsToDelete) {
      this.deleteAnnotation(annotation);
    }
    this.updateAnnotationIndexesAfterDelete(pagesToDelete);
  }

  /**
   * Updates the page number indexes after pages have been removed.
   *
   * @param deletedIndexes - Array of removed page indexes.
   */
  updateAnnotationIndexesAfterDelete(deletedIndexes: number[]) {
    for (const anno of this.annotations) {
      const count = deletedIndexes.filter((index) => index < anno.page).length;
      anno.page -= count;
    }
    this.redrawAnnotations(true);
    this.redrawThumbnailAnnotations(true);
  }

  /**
   * Update page indexes after insert.
   * @param startIndex - Start of the insert.
   * @param pageCount - Page count.
   */
  updateAnnotationPageIndexAfterInsert(startIndex: number, pageCount: number) {
    const count = 0;
    for (const annotation of this.annotations) {
      // Only adjusts indexes greater than or equal to the start index.
      if (annotation.page >= startIndex) {
        // Adjust page by the number of pages being added to the document.
        annotation.page += pageCount;
      }
    }
    this.redrawAnnotations(true);
  }

  /**
   * Parses annotations into the correct types.
   * @param annotations - Annotations.
   * @returns - Array of parsed annotations.
   */
  jsonParseAnnotations(annotations: DocumentAnnotations) {
    const parsedAnnotations = annotations
      .map((anno) => {
        if (this.isRectangleAnnotation(anno)) {
          anno.border.color = new Color(
            anno.border.color.red,
            anno.border.color.green,
            anno.border.color.blue,
            anno.border.color.alpha
          );
          return new RectangleAnnotation(
            anno.page,
            anno.x,
            anno.y,
            anno.height,
            anno.width,
            anno.border,
            new Color(
              anno.color.red,
              anno.color.green,
              anno.color.blue,
              anno.color.alpha
            ),
            anno.rotation,
            anno.visible,
            anno.uuid,
            anno.isHighlight
          );
        } else if (this.isStampAnnotation(anno)) {
          anno.border.color = new Color(
            anno.border.color.red,
            anno.border.color.green,
            anno.border.color.blue,
            anno.border.color.alpha
          );
          return new StampAnnotation(
            anno.page,
            anno.x,
            anno.y,
            anno.width,
            anno.height,
            anno.fontSize,
            anno.text,
            anno.uuid,
            new Color(
              anno.color.red,
              anno.color.green,
              anno.color.blue,
              anno.color.alpha
            ),
            anno.font,
            anno.rotation,
            anno.visible,
            anno.border
          );
        } else if (this.isTextAnnotation(anno)) {
          anno.border.color = new Color(
            anno.border.color.red,
            anno.border.color.green,
            anno.border.color.blue,
            anno.border.color.alpha
          );
          return new TextAnnotation(
            anno.page,
            anno.x,
            anno.y,
            anno.width,
            anno.height,
            anno.fontSize,
            anno.alignment,
            anno.text,
            new Color(
              anno.color.red,
              anno.color.green,
              anno.color.blue,
              anno.color.alpha
            ),
            anno.uuid,
            new Font(
              anno.font.name,
              anno.font.bold,
              anno.font.italic,
              anno.font.size,
              anno.font.strikeout,
              anno.font.underline
            ),
            anno.border,
            new Color(
              anno.fillColor.red,
              anno.fillColor.green,
              anno.fillColor.blue,
              anno.fillColor.alpha
            ),
            anno.rotation,
            anno.visible,
            undefined
          );
        } else if (this.isImageAnnotation(anno)) {
          return new ImageAnnotation(
            anno.page,
            anno.x,
            anno.y,
            anno.height,
            anno.width,
            anno.image,
            anno.imageHeight,
            anno.imageWidth,
            anno.rotation,
            anno.opacity,
            anno.visible,
            anno.uuid
          );
        } else if (this.isPathAnnotation(anno)) {
          return new PathAnnotation(
            anno.page,
            anno.x,
            anno.y,
            new Color(
              anno.color.red,
              anno.color.green,
              anno.color.blue,
              anno.color.alpha
            ),
            anno.thickness,
            anno.rotation,
            anno.visible,
            anno.pathPoints,
            anno.height,
            anno.width,
            anno.uuid
          );
        } else if (this.isLineAnnotation(anno)) {
          return new LineAnnotation(
            anno.page,
            anno.x,
            anno.y,
            anno.end,
            new LineAppearance(
              anno.line.caps,
              new Color(
                anno.line.color.red,
                anno.line.color.green,
                anno.line.color.blue,
                anno.line.color.alpha
              ),
              anno.line.style,
              anno.line.thickness,
              anno.line.endCap
            ),
            anno.length,
            anno.visible,
            anno.uuid,
            anno.rotation,
            anno.width,
            anno.height,
            anno.startcap,
            anno.endcap
          );
        } else if (this.isEllipseAnnotation(anno)) {
          return new EllipseAnnotation(
            anno.page,
            anno.x + anno.width / 2,
            anno.y + anno.height / 2,
            anno.xScale,
            anno.yScale,
            new LineAppearance(
              anno.border.caps,
              new Color(
                anno.border.color.red,
                anno.border.color.green,
                anno.border.color.blue,
                anno.border.color.alpha
              ),
              anno.border.style,
              anno.border.thickness
            ),
            new Color(
              anno.color.red,
              anno.color.green,
              anno.color.blue,
              anno.color.alpha
            ),
            anno.rotation,
            anno.visible,
            anno.uuid,
            anno.height,
            anno.width
          );
        }
      })
      .filter(
        (
          anno
        ): anno is RectangleAnnotation | StampAnnotation | TextAnnotation =>
          anno !== null
      );
    return parsedAnnotations;
  }

  /**
   * Returns if the annotation is a rectangle annotation.
   * @param annotation - Annotation .
   * @returns - Boolean.
   */
  isRectangleAnnotation(
    annotation: DocumentAnnotation
  ): annotation is RectangleAnnotation {
    return (
      !('font' in annotation) &&
      'border' in annotation &&
      !('xScale' in annotation)
    );
  }
  /**
   * Returns if the annotation is a stamp annotation.
   * @param annotation - Annotation .
   * @returns - Boolean.
   */
  isStampAnnotation(
    annotation: DocumentAnnotation
  ): annotation is StampAnnotation {
    return 'font' in annotation && !('fillColor' in annotation);
  }
  /**
   * Returns if the annotation is a text annotation.
   * @param annotation - Annotation.
   * @returns - Boolean.
   */
  isTextAnnotation(
    annotation: DocumentAnnotation
  ): annotation is TextAnnotation {
    return 'border' in annotation && 'fillColor' in annotation;
  }

  /**
   * Returns if the annotation is an image annotation.
   * @param annotation - Annotation.
   * @returns - Boolean.
   */
  isImageAnnotation(
    annotation: DocumentAnnotation
  ): annotation is ImageAnnotation {
    return 'image' in annotation;
  }

  /**
   * Returns if the annotation is a path annotation.
   * @param annotation - Annotation.
   * @returns - Boolean.
   */
  isPathAnnotation(
    annotation: DocumentAnnotation
  ): annotation is PathAnnotation {
    return 'pathPoints' in annotation;
  }

  /**
   * Returns if the annotation is a line annotation.
   * @param annotation - Annotation.
   * @returns - Boolean.
   */
  isLineAnnotation(
    annotation: DocumentAnnotation
  ): annotation is LineAnnotation {
    return 'end' in annotation;
  }

  /**
   * Returns if the anotation is an ellipse annotation.
   * @param annotation - Annotation.
   * @returns - Boolean
   */
  isEllipseAnnotation(
    annotation: DocumentAnnotation
  ): annotation is EllipseAnnotation {
    return 'xScale' in annotation && 'yScale' in annotation;
  }

  /**
   * Returns the annotations for saving.
   * @returns - Pages Of Document Annotations.
   */
  getAnnotationsForSave(): PagesOfDocumentAnnotations {
    const pagesOfDocumentAnnotations: PagesOfDocumentAnnotations = new Map();
    for (const annotation of this.annotations) {
      if (!pagesOfDocumentAnnotations.has(annotation.page)) {
        pagesOfDocumentAnnotations.set(annotation.page, []);
      }
      pagesOfDocumentAnnotations.get(annotation.page)!.push(annotation);
    }
    return pagesOfDocumentAnnotations;
  }
}
